import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('de');
  const [isLoading, setIsLoading] = useState(true);

  // Detect language based on geo-location and browser settings
  useEffect(() => {
    const detectLanguageByGeoAndBrowser = async () => {
      // Check if language is manually set in localStorage
      const storedLanguage = localStorage.getItem('brovko_language');
      const isManuallySet = localStorage.getItem('brovko_language_manual');
      
      if (storedLanguage && isManuallySet === 'true') {
        return storedLanguage;
      }

      // Die Browsersprache entscheidet. Frueher lief das ueber eine Abfrage
      // beim Standortdienst - der bekam die Besucher-IP vor der Einwilligung
      // zu sehen, nur um zwischen zwei Sprachen zu waehlen.
      const browserLanguage = navigator.language || navigator.languages?.[0] || 'de';
      const languageCode = browserLanguage.split('-')[0].toLowerCase();
      const countryCode = browserLanguage.split('-')[1]?.toUpperCase();

      if (languageCode === 'de' || ['DE', 'AT', 'CH'].includes(countryCode)) {
        return 'de';
      }

      return 'en';
    };

    detectLanguageByGeoAndBrowser().then(detectedLanguage => {
      setCurrentLanguage(detectedLanguage);
      if (!localStorage.getItem('brovko_language_manual')) {
        localStorage.setItem('brovko_language', detectedLanguage);
      }
      setIsLoading(false);
    });
  }, []);

  const switchLanguage = (language) => {
    if (language === 'de' || language === 'en') {
      setCurrentLanguage(language);
      localStorage.setItem('brovko_language', language);
      // Mark as manually set to prevent auto-detection override
      localStorage.setItem('brovko_language_manual', 'true');
    }
  };

  const t = (key, language = currentLanguage) => {
    return translations[language]?.[key] || key;
  };

  const value = {
    currentLanguage,
    switchLanguage,
    t,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translation strings
const translations = {
  de: {
    // Navigation
    'nav.home': 'Home',
    'nav.valueEngine': 'KI-Value Engine',
    'nav.services': 'Leistungsangebot',
    'nav.caseStudies': 'Fallstudien',
    'nav.knowledgeCenter': 'Wissenscenter',
    'nav.about': 'Über uns',
    'nav.clients': 'Kunden',
    'nav.spinoffs': 'Unsere Spin-Offs',
    'nav.products': 'Produkte',
    'nav.dropdown.allProducts': 'Alle Produkte',
    'nav.dropdown.granalis': 'Granalis',
    'nav.dropdown.vox2doc': 'Vox2Doc',
    'nav.dropdown.domicus': 'Domicus',
    'nav.dropdown.fleetdoc': 'Fleetdoc',
    'nav.dropdown.fairspark': 'Fairspark',
    'nav.dropdown.replyly': 'Replyly',
    'nav.search': 'Suchen...',
    'nav.solutions': 'Lösungen',
    'nav.enablement': 'Enablement',
    'nav.expertise': 'Expertise',
    'nav.ctaButton': 'KI-Value Check starten',
    
    // KI-Value Engine Navigation Dropdown
    'nav.dropdown.theSystem': 'Die Engine',
    'nav.dropdown.valueCheck': 'KI-Value Check',
    'nav.dropdown.valueDashboard': 'Value-Dashboard',
    'nav.dropdown.sovereignCloud': 'KI-Souveränität',

    // Solutions Navigation Dropdown
    'nav.dropdown.roiImpact': 'ROI & Business Impact',
    'nav.dropdown.transformation': 'Strategische KI-Transformation',
    'nav.dropdown.privateEquity': 'Private Equity',

    // Enablement Navigation Dropdown
    'nav.dropdown.academy': 'KI-Academy',
    'nav.dropdown.workshops': 'KI-Workshops',
    'nav.dropdown.executiveCoaching': 'KI Executive Coaching',

    // Expertise Navigation Dropdown
    'nav.dropdown.whitepapersStudies': 'Whitepaper & Studien',
    'nav.dropdown.useCases': 'Use Cases',
    'nav.dropdown.aiwizzPlatform': 'AIWizz Plattform',

    // Legacy - keep for compatibility
    'nav.dropdown.coaching': 'KI-Coaching',
    'nav.dropdown.applications': 'KI-Anwendungen',
    'nav.dropdown.platform': 'KI-Plattform',
    'nav.dropdown.mvp': 'KI-MVP-Entwicklung',
    'nav.dropdown.kilab': 'Externes KI-Lab',

    // Hero Section
    'hero.badge': 'KI-Roadmap in 30 Tagen',
    'hero.title.part1': 'Ihr One-Stop-Partner für',
    'hero.title.part2': 'KI-Transformation',
    'hero.title.part3': '– von der Strategie bis zur produktiven Anwendung.',
    'hero.subtitle': 'Mit der einzigartigen BROVKO Tool Box und mehr als 20 Top Entwicklern',
    'hero.cta': 'KI-Transformation starten',

    // Strategy Section
    'strategy.title.part1': 'KI ist kein Tool-Thema.',
    'strategy.title.part2': 'Es ist ein Transformations-Thema.',
    'strategy.subtitle': 'In einer Welt, in der technische KI-Setups immer einfacher und günstiger werden, fehlt vielen Unternehmen das Entscheidende:',
    'strategy.point1.title': 'Klarheit über den tatsächlichen Nutzen',
    'strategy.point1.desc': 'Wir helfen Ihnen dabei, den echten Wert von KI für Ihr Unternehmen zu identifizieren.',
    'strategy.point2.title': 'Ordnungsrahmen für die eigene Organisation',
    'strategy.point2.desc': 'Strukturierte Herangehensweise für eine erfolgreiche KI-Implementation.',
    'strategy.point3.title': 'Fahrplan für echte Wirkung',
    'strategy.point3.desc': 'Von der Technologie zu messbaren Geschäftsergebnissen.',

    // Solutions Section
    'solutions.title': 'Unsere KI-Lösungen für Ihr Unternehmen',
    'solutions.subtitle': 'Wir bieten flexible, auf Ihre Bedürfnisse zugeschnittene KI-Lösungen. Wählen Sie den passenden Einstieg für Ihr Unternehmen:',
    'solutions.workshops.title': 'KI-Transformation',
    'solutions.workshops.desc': 'Strategische Transformation und nachhaltige Veränderung.',
    'solutions.consulting.title': 'KI-Akademie',
    'solutions.consulting.desc': 'Lernen. Anwenden. Transformieren.',
    'solutions.applications.title': 'KI-Anwendungen',
    'solutions.applications.desc': 'Maßgeschneiderte KI-Projekte für Ihr Unternehmen.',
    'solutions.platforms.title': 'KI-Plattformen',
    'solutions.platforms.desc': 'Skalierbare, modulare KI-Systeme.',
    'solutions.learnMore': 'Mehr dazu...',

    // Benefits Section
    'benefits.title': 'Wir bringen Ordnung, Struktur und Wirkung in Ihre KI-Initiativen.',
    'benefits.compass.title': 'KI-Kompass',
    'benefits.compass.desc': 'Orientierung & strategische Ausrichtung',
    'benefits.analysis.title': 'Potenzial-Analyse',
    'benefits.analysis.desc': 'Identifikation & Priorisierung wirkungsvoller Use Cases',
    'benefits.canvas.title': 'KI-Canvas',
    'benefits.canvas.desc': 'Ganzheitliche Planung und Steuerung Ihrer KI-Aktivitäten',
    'benefits.playbook.title': 'Prompt Playbook',
    'benefits.playbook.desc': 'Enablement für Mitarbeitende & Führungskräfte',
    'benefits.matrix.title': 'Zeitfresser-Matrix',
    'benefits.matrix.desc': 'Effizienzsteigerung durch Automatisierung von Routinetätigkeiten',
    'benefits.factory.title': 'Use Case Factory',
    'benefits.factory.desc': 'Bewährte Schablonen für die schnelle Umsetzung von über 30 KI-Use-Cases',
    'benefits.cta': 'Erfahren Sie mehr über unsere KI-Erfolgsgeschichten →',

    // Achievements
    'achievements.title': 'Was Sie mit BROVKO erreichen',
    'achievements.clarity.title': 'Klarheit über Strategie, Prioritäten und Wirkung',
    'achievements.clarity.desc': 'Strukturierte Herangehensweise für nachhaltige KI-Transformation',
    'achievements.speed.title': 'Schnelligkeit durch vorkonzipierte Tools & Methoden',
    'achievements.speed.desc': 'Bewährte Frameworks beschleunigen Ihre Implementierung',
    'achievements.impact.title': 'Wirkung durch konkrete Ergebnisse – oft in weniger als 90 Tagen',
    'achievements.impact.desc': 'Messbare Erfolge in kurzer Zeit durch fokussierte Umsetzung',
    'achievements.security.title': 'Sicherheit durch strukturierte Begleitung und Know-how-Aufbau',
    'achievements.security.desc': 'Experten mit tiefem KI- & Branchen-Know-how an Ihrer Seite',

    // Target Audience
    'audience.title': 'Für wen wir arbeiten',
    'audience.clevel.title': 'C-Level & Strategieeinheiten',
    'audience.clevel.desc': 'Roadmaps, Steuerung, Governance',
    'audience.clevel.details': 'Strategische Führung und Governance-Strukturen für erfolgreiche KI-Transformation auf Unternehmensebene.',
    'audience.enterprise.title': 'Mittelstand & Großunternehmen',
    'audience.enterprise.desc': 'Readiness, interne Use Cases, Skalierung',
    'audience.enterprise.details': 'Umfassende Begleitung von der ersten Analyse bis zur unternehmensweiten Skalierung von KI-Lösungen.',
    'audience.pe.title': 'Private Equity',
    'audience.pe.desc': 'Portfolio Enablement, Quick-Wins, Exit-Vorbereitung',
    'audience.pe.details': 'Wertsteigerung durch strategische KI-Implementation in Portfolio-Unternehmen für optimale Exit-Szenarien.',

    // Resources
    'resources.subtitle': 'Von maßgeschneiderten KI-Lösungen bis hin zu wertvollen Ressourcen – hier finden Sie alles, was Sie für Ihre digitale Transformation benötigen.',
    'resources.expertise.title': 'Unsere Expertise',
    'resources.expertise.desc': 'Erfahren Sie, wie Brovko strategische KI-Beratung und schlüsselfertige Lösungen bietet, die Ihr Unternehmen auf das nächste Level bringen.',
    'resources.caseStudies.title': 'Projekte und Fallstudien',
    'resources.caseStudies.desc': 'Sehen Sie, wie wir Unternehmen in verschiedenen Branchen durch innovative KI-Lösungen zum Erfolg verholfen haben.',
    'resources.whitepapers.title': 'White Papers & Ressourcen',
    'resources.whitepapers.desc': 'Laden Sie unsere neuesten White Papers herunter, um tiefer in die Welt der KI-Transformation einzutauchen.',
    'resources.aiwizz.title': 'AIWizz - Our Product Brand',
    'resources.aiwizz.desc': 'Entdecken Sie unsere eigenständige Marke für schlüsselfertige KI-Lösungen, basierend auf jahrelanger Erfahrung.',
    'resources.books.title': 'Unser Bücher',
    'resources.books.desc': 'Lesen Sie unser Buch "KI-Revolution in Unternehmen" und erfahren Sie, wie KI Ihre Branche transformieren kann.',
    'resources.coaching.title': 'KI-Coaching & KI-Beirat',
    'resources.coaching.desc': 'Maßgeschneiderte KI-Coachings für Führungskräfte, die Ihre Organisation erfolgreich durch die digitale Transformation leiten möchten.',

    // CTA Section
    'cta.title': 'Die Zeit der POCs ist vorbei.',
    'cta.subtitle': 'Jetzt braucht es Umsetzung mit Wirkung.',
    'cta.description1': 'BROVKO liefert Klarheit und Struktur in einer komplexen KI-Welt. Wir verbinden Methoden, Technologie und strategische Steuerung – individuell, pragmatisch, wirksam.',
    'cta.description2': 'Kein Buzzword. Kein Overengineering. Sondern: Wirkung. Auch mit AIWizz – unseren sofort einsatzfähigen KI-Plugins für Support, Forecasting, Sales u.v.m.',
    'cta.button': 'Jetzt kostenloses Erstgespräch buchen',

    // Clients Section
    'clients.title': 'Unsere Kunden',
    'clients.subtitle': 'Ausgewählte Unternehmen, mit denen wir in verschiedenen Projekten zusammengearbeitet haben.',
    'clients.stat1': '100+ erfolgreiche Projekte',
    'clients.stat2': '50+ zufriedene Kunden',
    'clients.stat3': '5+ Jahre KI-Expertise',

    // Newsletter
    'newsletter.badge': 'Newsletter',
    'newsletter.title': 'Melden Sie sich für unseren Newsletter an',
    'newsletter.subtitle': 'Bleiben Sie auf dem Laufenden über die neuesten KI-Trends, Best Practices und Erfolgsgeschichten aus der Praxis.',
    'newsletter.firstName': 'Vorname',
    'newsletter.lastName': 'Nachname',
    'newsletter.email': 'E-Mail-Adresse',
    'newsletter.consent': 'Ich habe die Datenschutzerklärung zur Kenntnis genommen und stimme der Verarbeitung meiner Daten zu.',
    'newsletter.privacy': 'Datenschutzerklärung',
    'newsletter.subscribe': 'Abonnieren',
    'newsletter.submitting': 'Anmelden...',
    'newsletter.success': 'Erfolgreich angemeldet!',
    'newsletter.successDesc': 'Vielen Dank für das Abonnement unseres Newsletters.',
    'newsletter.error': 'Fehler beim Anmelden',
    'newsletter.errorDesc': 'Bitte versuchen Sie es später erneut.',
    'newsletter.fillFields': 'Bitte füllen Sie alle Felder aus',
    'newsletter.fillFieldsDesc': 'Alle Felder sind erforderlich.',
    'newsletter.consentRequired': 'Datenschutzerklärung erforderlich',
    'newsletter.consentRequiredDesc': 'Bitte akzeptieren Sie die Datenschutzerklärung.',
    'newsletter.benefit1': 'Monatliche Insights',
    'newsletter.benefit2': 'Exklusive Inhalte',
    'newsletter.benefit3': 'Jederzeit kündbar',

    // ChatBot (removed)
    

    // Footer
    'footer.company': 'Ihr Partner für KI-Transformation. Wir bringen Ordnung, Struktur und messbare Wirkung in Ihre KI-Initiativen – von der Strategie bis zur Umsetzung.',
    'footer.companyName': 'BROVKO GmbH',
    'footer.companyDesc1': 'Strategische KI-Beratung',
    'footer.companyDesc2': 'Deutschland',
    'footer.quickLinks': 'Schnellzugriff',
    'footer.services': 'Leistungen',
    'footer.caseStudies': 'Fallstudien',
    'footer.knowledgeCenter': 'Wissenscenter',
    'footer.about': 'Über uns',
    'footer.peWhitepaper': 'PE White Papers',
    'footer.orgWhitepaper': 'KI & Organisation',
    'footer.partnerProgram': 'Partnerprogramm für IT-Dienstleister',
    'footer.contact': 'Kontakt',
    'footer.contactInfo': 'Kontakt',
    'footer.email': 'email@brovko.de',
    'footer.website': 'www.brovko.de',
    'footer.copyright': `© ${new Date().getFullYear()} Brovko GmbH. Alle Rechte vorbehalten.`,
    'footer.imprint': 'Impressum',
    'footer.privacy': 'Datenschutz',
    'footer.cookieNotice': 'Wir verwenden Cookies auf unserer Website, um zu sehen, wie Sie mit ihr interagieren. Durch die Annahme stimmen Sie der Verwendung solcher Cookies zu.',
    'footer.privacyPolicy': 'Datenschutzerklärung',
    'footer.settings': 'Einstellungen',
    'footer.decline': 'Ablehnen',
    'footer.accept': 'Akzeptieren',

    // Services Page
    'services.hero.title': 'Unsere KI-Leistungen',
    'services.hero.subtitle': 'Maßgeschneiderte KI-Lösungen für nachhaltigen Unternehmenserfolg. Von der strategischen Planung bis zur praktischen Umsetzung.',
    'services.hero.experience': '5+ Jahre KI-Expertise',
    'services.overview.title': 'Unsere Leistungsbereiche',
    'services.overview.subtitle': 'Wählen Sie den passenden Einstieg für Ihr Unternehmen. Alle Leistungen sind individuell an Ihre Bedürfnisse anpassbar.',
    
    'services.transformation.title': 'KI-Transformation',
    'services.transformation.description': 'Ganzheitliche KI-Transformation für nachhaltige Geschäftsergebnisse und strategische Wettbewerbsvorteile.',
    'services.transformation.feature1': 'Strategische KI-Roadmap',
    'services.transformation.feature2': 'Change Management',
    'services.transformation.feature3': 'Kultureller Wandel',
    'services.transformation.feature4': 'Messbare Transformation',
    
    'services.consulting.title': 'KI-Akademie',
    'services.consulting.description': 'Umfassende Bildungsprogramme und praxisnahe Trainings für nachhaltige KI-Kompetenzentwicklung.',
    'services.consulting.feature1': 'C-Level Coaching',
    'services.consulting.feature2': 'EU AI Act & Governance',
    'services.consulting.feature3': 'Praxisnahe Trainings',
    'services.consulting.feature4': 'Individuelle KI-Kurse',
    
    'services.coaching.title': 'KI-Coaching & KI-Beirat',
    'services.coaching.description': 'Persönliche Begleitung auf C-Level für strategische KI-Entscheidungen und Umsetzung.',
    'services.coaching.feature1': '1:1 Coaching für Führungskräfte',
    'services.coaching.feature2': 'AI-Beirat & AI Committee',
    'services.coaching.feature3': 'Strategisches Sparring',
    'services.coaching.feature4': 'Board-Reportings & KPIs',
    
    'services.workshops.title': 'KI-Workshops',
    'services.workshops.description': 'Praxisnahe Workshops für schnelle Ergebnisse und direkt umsetzbare Handlungsempfehlungen.',
    'services.workshops.feature1': 'KI-Strategie-Workshop',
    'services.workshops.feature2': 'KI-Potentialanalyse',
    'services.workshops.feature3': 'Live-Demos & Use Cases',
    'services.workshops.feature4': 'Digitale KI-Checklisten',
    
    'services.platform.title': 'KI-Plattform',
    'services.platform.description': 'AIWizz - Ihre private GPT-Umgebung für maximale Produktivität',
    'services.platform.feature1': 'Private GPT ohne Datenübertragung',
    'services.platform.feature2': 'Neueste LLM-Technologien (GPT, Claude, Gemini)',
    'services.platform.feature3': 'Multi-Agenten-Systeme für alle Abteilungen',
    'services.platform.feature4': 'DSGVO-konform und höchste Sicherheit',
    
    'services.applications.title': 'KI-Anwendungen',
    'services.applications.description': 'Maßgeschneiderte KI-Projekte und schlüsselfertige Lösungen für Ihr Unternehmen.',
    'services.applications.feature1': 'Custom KI-Entwicklung',
    'services.applications.feature2': 'Integration bestehender Systeme',
    'services.applications.feature3': 'Skalierbare Architekturen',
    'services.applications.feature4': 'Wartung & Support',

    'services.mvp.title': 'KI-MVP-Entwicklung',
    'services.mvp.description': 'Ihr funktionierendes KI-MVP in nur 3-4 Wochen zum Festpreis. Schnelle, risikoarme Umsetzung Ihrer KI-Idee.',
    'services.mvp.feature1': 'Festpreis von 19.000 EUR',
    'services.mvp.feature2': 'Lieferung in 3-4 Wochen',
    'services.mvp.feature3': 'Sofort testbarer Nutzen',
    'services.mvp.feature4': 'Minimiertes Risiko',
    'services.mvp.highlight': 'NEU',
    'services.mvp.cta': 'MVP jetzt starten',

    'services.kilab.title': 'Externes KI-Lab',
    'services.kilab.description': 'Ihr ausgelagertes KI-Team für kontinuierliche Entwicklung und Know-how-Transfer bis zur eigenen KI-Souveränität.',
    'services.kilab.feature1': 'Sofort verfügbares KI-Team',
    'services.kilab.feature2': 'Fixe Kosten statt Gehälter',
    'services.kilab.feature3': '4-Phasen-Modell bis zur Eigenständigkeit',
    'services.kilab.feature4': 'Flexibler Ausstieg möglich',
    
    'services.learnMore': 'Mehr erfahren',
    
    'services.why.title': 'Warum BROVKO?',
    'services.why.subtitle': 'Unser Ansatz kombiniert strategische Klarheit mit praktischer Umsetzung für messbare Ergebnisse.',
    'services.why.expertise.title': 'Expertise',
    'services.why.expertise.description': '5+ Jahre KI-Erfahrung in verschiedenen Branchen',
    'services.why.practical.title': 'Praxisnah',
    'services.why.practical.description': 'Direkt umsetzbare Lösungen statt Theorie',
    'services.why.results.title': 'Ergebnisorientiert',
    'services.why.results.description': 'Messbare Erfolge oft in weniger als 90 Tagen',
    'services.why.support.title': 'Begleitung',
    'services.why.support.description': 'Strukturierte Unterstützung vom Start bis zum Erfolg',
    
    'services.cta.title': 'Bereit für Ihre KI-Transformation?',
    'services.cta.subtitle': 'Lassen Sie uns gemeinsam Ihre KI-Strategie entwickeln und erfolgreich umsetzen.',
    'services.cta.button': 'Kostenloses Erstgespräch buchen',

    // About Page  
    'about.hero.title': 'Über BROVKO',
    'about.hero.subtitle': 'Lernen Sie unser Team, unsere Mission und unsere Vision kennen. Entdecken Sie, wie wir Unternehmen mit maßgeschneiderten KI-Lösungen zum Erfolg verhelfen.',
    
    'about.team.title': 'Das Brovko Team',
    'about.team.description': 'Unser Team besteht aus mehr als 20 erfahrenen Spezialisten, die tiefgreifende Expertise in KI-Projekten und Transformationsprozessen mitbringen. Unsere Experten haben in verschiedenen Branchen wie Handel, Produktion, Banken und Versicherungen gearbeitet und verfügen über fundierte Erfahrungen als CIOs und CDOs. Mit diesem Know-how unterstützen wir Sie bei der erfolgreichen Implementierung maßgeschneiderter KI-Lösungen.',
    'about.team.specialists': 'Erfahrene Spezialisten',
    'about.team.expertise': 'CIOs, CDOs und KI-Experten',
    
    'about.services.title': 'BROVKO Services',
    'about.services.description': 'Mit über einem Jahrzehnt Erfahrung in der Beratung führender Unternehmen auf nationaler und internationaler Ebene haben wir umfassende Kompetenz in Digitalisierung, IT-/Digitalisierungs-Strategie und Künstlicher Intelligenz entwickelt. Unsere Expertise umfasst Strategie-Entwicklung, Schulungen und operative Umsetzung. Wir haben fundiertes Verständnis für Prozesse und Herausforderungen in Marketing, Vertrieb, CRM, Produkt-Management, Logistik, Produktion und HR. Diese Kombination aus IT- und KI-Expertise sowie Fachbereichskenntnissen macht uns zum idealen Partner für Ihre erfolgreiche KI-Transformation.',
    'about.services.button': 'Unsere KI-Leistungen',
    
    'about.mission.title': 'Unsere Mission',
    'about.mission.description': 'Unsere Mission ist es, Unternehmen mit Hilfe modernster KI-Technologien zukunftssicher und wettbewerbsfähig zu machen. Wir glauben daran, dass KI der Schlüssel zur nachhaltigen Transformation ist und möchten Ihnen zeigen, wie diese Technologie Ihr Geschäft revolutionieren kann.',
    
    'about.vision.title': 'Unsere Vision',
    'about.vision.description': 'Wir streben an, führend in der strategischen KI-Beratung zu sein, um Unternehmen in Deutschland und international zu helfen, die Chancen der digitalen Transformation voll auszuschöpfen. Unser Ziel ist es, Ihre erste Wahl für innovative, strategische KI-Lösungen zu sein.',
    
    'about.values.title': 'Unsere Werte',
    'about.values.subtitle': 'Diese Prinzipien leiten unser Handeln und prägen unsere Zusammenarbeit mit Ihnen.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description': 'Wir setzen auf kontinuierliche Innovation, um die besten und neuesten KI-Technologien anzubieten.',
    'about.values.excellence.title': 'Exzellenz',
    'about.values.excellence.description': 'Qualität und Exzellenz sind die Grundlagen unseres Handelns. Wir liefern stets erstklassige Lösungen und Dienstleistungen.',
    'about.values.customer.title': 'Kundenzentriertheit',
    'about.values.customer.description': 'Unsere Kunden stehen im Mittelpunkt. Wir arbeiten eng mit ihnen zusammen, um maßgeschneiderte Lösungen zu entwickeln, die ihren individuellen Bedürfnissen gerecht werden.',
    'about.values.integrity.title': 'Integrität',
    'about.values.integrity.description': 'Ehrlichkeit und Transparenz sind uns wichtig. Wir handeln stets ethisch und verantwortungsbewusst.',
    'about.values.teamwork.title': 'Teamarbeit',
    'about.values.teamwork.description': 'Wir glauben an die Kraft der Zusammenarbeit. Unser Team aus Experten arbeitet gemeinsam, um die besten Ergebnisse zu erzielen.',
    
    'about.history.title': 'Unternehmensgeschichte',
    'about.history.founding': 'Gründung',
    'about.history.description': 'Seit unserer Gründung im Jahr 2011 begleiten wir Unternehmen in nahezu allen Geschäftsbereichen und Industrien auf ihrem Weg zur erfolgreichen digitalen Transformation. Mit unserer breiten Branchenexpertise verstehen wir die vielfältigen Herausforderungen unserer Kunden und entwickeln individuelle, innovative Lösungen, die ihnen helfen, wettbewerbsfähig zu bleiben und nachhaltige Erfolge zu erzielen. Dabei legen wir besonderen Wert auf praxisorientierte Ansätze, die messbare Ergebnisse liefern. Unser Ziel ist es, Unternehmen langfristig zu stärken und sie für die Zukunft bestens aufzustellen.',
    
    'about.resources.title': 'Unsere Ressourcen',
    'about.resources.subtitle': 'Mit unserem Headquarter in Hannover, unserem Business Excellence Center in Düsseldorf, einem großen, kompetenten Entwicklungsteam in Breslau, Polen, und unserem KI-Think Tank in Haifa, Israel, betreuen wir sowohl nationale als auch internationale Projekte. Diese globale Struktur ermöglicht es uns, maßgeschneiderte Lösungen für Unternehmen weltweit zu entwickeln und umzusetzen.',
    'about.locations.germany': 'Deutschland',
    'about.locations.poland': 'Polen',
    'about.locations.israel': 'Israel',
    'about.locations.hannover.role': 'Headquarter',
    'about.locations.hannover.description': 'Unser Headquarter in Hannover ist das Herzstück unserer strategischen Unternehmensentwicklung. Von hier aus steuern wir die Planung und Ausrichtung unseres Unternehmens, entwickeln innovative KI-Strategien und arbeiten eng mit unseren Kunden an der Umsetzung ihrer digitalen Transformation. Zusätzlich koordinieren wir von diesem Standort aus alle operativen Aktivitäten, um sicherzustellen, dass unsere Projekte reibungslos und erfolgreich verlaufen.',
    'about.locations.dusseldorf.role': 'Business Excellence',
    'about.locations.dusseldorf.description': 'Unser Standort in Düsseldorf ist das Zentrum für Business Excellence. Hier arbeiten wir daran, höchste Standards in allen Geschäftsbereichen zu setzen und gewährleisten, dass unsere Kunden von erstklassigen Strategien und operativer Exzellenz profitieren. Dieser Standort spielt eine zentrale Rolle in der Umsetzung unserer unternehmerischen Vision, nachhaltigen Erfolg und Spitzenleistung in allen Projekten sicherzustellen.',
    'about.locations.breslau.role': 'Software Development',
    'about.locations.breslau.description': 'Unser Standort in Breslau, Polen, beherbergt ein hochqualifiziertes Team von Software Experten, darunter Softwareentwickler, Data Scientists, Data Engineers und KI-Spezialisten. Dieses Team arbeitet an der Entwicklung innovativer, maßgeschneiderter Lösungen und unterstützt unsere Kunden weltweit mit fortschrittlichen Technologien und fundiertem Fachwissen.',
    'about.locations.haifa.role': 'AI-Think Tank & Trendscouting',
    'about.locations.haifa.description': 'Der Standort in Israel und unsere Partner sind unser KI-Think Tank und Trend Scouting Bereich, der die aktuell Entwicklung der KI beobachtet und bewertet, die nächsten Schritte in der KI-Entwicklung antizipiert und bei der Entwicklung von neuen Ideen, Use Cases und Geschäftsmodellen sowohl als Impulsgeber, als auch als Sparring Partner fungiert.',
    
    'about.cta.title': 'Lernen Sie uns kennen',
    'about.cta.subtitle': 'Überzeugen Sie sich selbst von unserer Expertise und unserem Ansatz.',
    'about.cta.button': 'Gespräch vereinbaren',

    // Knowledge Center
    'knowledge.hero.title': 'KI-Bibliothek: Ihr Wissenszentrum für Künstliche Intelligenz',
    'knowledge.hero.subtitle': 'Entdecken Sie umfassende White Papers, Use-Cases und Case-Studies, um die Potenziale von KI in Ihrem Unternehmen voll auszuschöpfen.',
    
    'knowledge.books.title': 'Unsere aktuellen Bücher',
    'knowledge.books.amazonButton': 'Jetzt auf Amazon',
    'knowledge.books.book1.title': 'KI-Revolution in Unternehmen',
    'knowledge.books.book1.subtitle': 'Chancen-Fallbeispiele-Erfolgsfaktoren',
    'knowledge.books.book1.description': 'Entdecken Sie in unserem neuen Buch, wie Künstliche Intelligenz Unternehmen transformiert und Wettbewerbsvorteile schafft. Erhalten Sie praxisnahe Einblicke, Fallbeispiele und Strategien, um die Potenziale von KI optimal zu nutzen.',
    'knowledge.books.book2.title': 'KI-Potentialanalyse in Unternehmen',
    'knowledge.books.book2.subtitle': 'Wie Unternehmen mit einer KI-Potentialanalyse Chancen erkennen, Prioritäten setzen, die Umsetzung planen und echten Mehrwert schaffen',
    'knowledge.books.book2.description': 'Das Buch richtet sich gezielt an C-Level-Management, Entscheider, Bereichsleiter und KI-Strategen – und zeigt Schritt für Schritt, wie eine professionelle KI-Potenzialanalyse durchgeführt wird.',
    
    'knowledge.whitepapers.title': 'Entdecken Sie unsere White Papers',
    'knowledge.whitepapers.subtitle': 'Laden Sie unsere neuesten White Papers herunter und erhalten Sie wertvolle Einblicke in KI-Strategien und Best Practices.',
    'knowledge.whitepapers.wp1.title': 'Künstliche Intelligenz in Unternehmen: Erfolgsfaktoren und Fallstudien',
    'knowledge.whitepapers.wp2.title': 'Chief Artificial Intelligence Officer (CAIO) als Architekt und Wegbereiter der KI-Transformation',
    'knowledge.whitepapers.wp3.title': 'Der CEO bzw. der Geschäftsführer als Treiber der KI-Transformation',
    'knowledge.whitepapers.wp4.title': 'KI-Einsatz in der Private Equity und M&A- Branche',
    'knowledge.whitepapers.wp5.title': 'Generative KI: Der Game Changer für Unternehmen',
    'knowledge.whitepapers.wp6.title': 'Process Intelligence: Prozessoptimierung mit Künstlicher Intelligenz (KI)',
    'knowledge.whitepapers.wp7.title': 'Maximale Wertschöpfung durch KI: Wie Private Equity Unternehmen den Wert ihrer Portfolio-Unternehmen deutlich steigern können',
    'knowledge.whitepapers.wp8.title': 'KI-gestützte Prognosen: Präzision neu definiert – Wie Künstliche Intelligenz Vorhersagen revolutioniert',
    'knowledge.whitepapers.wp9.title': 'KI-Einsatz in der Automobilindustrie',
    'knowledge.whitepapers.wp10.title': 'Jetzt mit KI durchstarten: Die Bedenken aus der aktuellen KI-Ifo-Studie und welche Lösungen es hierfür gibt',
    'knowledge.whitepapers.wp11.title': 'KI im E-Commerce: Wie Künstliche Intelligenz das Einkaufserlebnis und die Effizienz revolutioniert',
    'knowledge.whitepapers.wp12.title': 'Die Zukunft des Einzelhandels: Erfolgsfaktor Künstliche Intelligenz',
    'knowledge.whitepapers.wp13.title': 'Mit Künstlicher Intelligenz Kosten reduzieren: Ein praktischer Ansatz für Unternehmen',
    'knowledge.whitepapers.wp14.title': 'Umsatzsteigerung durch KI: Potenziale, Strategien und praxisorientierte Ansätze',
    'knowledge.whitepapers.wp15.title': 'Data Intelligence: Mit KI die Unternehmensdaten zu Daten-Gold machen',
    'knowledge.whitepapers.wp16.title': 'Erfolg durch KI: Strategische Innovationen in der Preisgestaltung',
    'knowledge.whitepapers.wp17.title': 'Next-Level-Kundenportale mit Künstlichen Intelligenz (KI)',
    'knowledge.whitepapers.wp18.title': 'KI-Transformation: Von der KI-Strategie zum KI Value- Champion',
    'knowledge.whitepapers.wp19.title': 'KI-Strategie und KI-Governance: Basis für die Transformation in Richtung KI-Mastery',
    'knowledge.whitepapers.wp20.title': 'KI-Technik & Architektur – Das Fundament für skalierbare KI Anwendungen (private LLM für Unternehmen)',
    'knowledge.whitepapers.wp21.title': '7 KI-Fähigkeiten, die Sie beherrschen MÜSSEN – oder im Wettbewerb zurückbleiben',
    'knowledge.whitepapers.wp22.title': 'Warum jetzt mit KI anfangen?!',
    'knowledge.whitepapers.wp23.title': 'AI-Impact nach 90 / 120 / 150 Tagen – Was Sie von KI-Projekten heute erwarten dürfen',
    'knowledge.whitepapers.wp24.title': 'Unternehmenseigene KI als zentrale Orchestrierungseinheit für Systeme, Prozesse und Wissen',
    'knowledge.whitepapers.wp25.title': 'KI in der Bank – Education meets Experience',
    'knowledge.whitepapers.wp26.title': 'Der Brovko-Ansatz für KI in Private Equity (PE)-Unternehmen',
    'knowledge.whitepapers.wp27.title': 'KI in der Schweiz – sicher, konkret, wirksam',
    'knowledge.whitepapers.wp28.title': 'Künstliche Intelligenz in Österreich – so gelingt der praxisnahe Einstieg',
    'knowledge.whitepapers.wp29.title': 'Künstliche Intelligenz in Aktion – 5 realistische Einsatzfelder für Private Equity',
    'knowledge.whitepapers.wp30.title': 'AIWizz – Die Enterprise KI-Plattform für Wirkung, Sicherheit und Skalierung',
    'knowledge.whitepapers.wp31.title': 'KI im Vertrieb',
    'knowledge.whitepapers.wp32.title': 'Fachkräftemangel? Mit KI intelligent entlasten – statt Personal zu ersetzen',
    'knowledge.whitepapers.wp33.title': 'AIWizz - Die neue Steuerzentrale für Ihr Unternehmen: Ihre eigene KI. Ihre Regeln. Ihre Wirkung',
    'knowledge.whitepapers.wp34.title': 'AIWizz für Private Equity',
    'knowledge.whitepapers.wp35.title': 'Best Practices in der Schweiz',
    'knowledge.whitepapers.wp36.title': 'ChatGPT 5 verändert alles',
    
    'knowledge.download.title': 'White Paper herunterladen',
    'knowledge.download.subtitle': 'Bitte geben Sie Ihre Kontaktdaten ein, um das White Paper herunterzuladen.',
    'knowledge.download.firstName': 'Vorname',
    'knowledge.download.lastName': 'Nachname',
    'knowledge.download.email': 'E-Mail-Adresse',
    'knowledge.download.company': 'Unternehmen (optional)',
    'knowledge.download.cancel': 'Abbrechen',
    'knowledge.download.submit': 'Herunterladen',
    'knowledge.download.submitting': 'Wird verarbeitet...',
    'knowledge.download.success': 'Vielen Dank! Das White Paper wird heruntergeladen.',
    'knowledge.download.successTitle': 'Download bereit!',
    'knowledge.download.successMessage': 'Vielen Dank für Ihr Interesse. Ihr White Paper ist zum Download bereit.',
    'knowledge.download.downloadNow': 'Jetzt herunterladen',
    'knowledge.download.downloadStarted': 'Download gestartet!',
    'knowledge.download.downloadError': 'Download fehlgeschlagen. Bitte versuchen Sie es später erneut.',
    'knowledge.download.close': 'Schließen',
    'knowledge.download.errorMessage': 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    'knowledge.download.privacy': 'Ihre Daten werden vertraulich behandelt',
    'knowledge.download.errors.firstName': 'Vorname ist erforderlich',
    'knowledge.download.errors.lastName': 'Nachname ist erforderlich',
    'knowledge.download.errors.email': 'E-Mail-Adresse ist erforderlich',
    'knowledge.download.errors.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'knowledge.download.errors.company': 'Bitte geben Sie Ihre Firma ein',
    'knowledge.download.errors.phone': 'Bitte geben Sie Ihre Telefonnummer ein',
    
    'knowledge.cta.title': 'Benötigen Sie individuelle KI-Beratung?',
    'knowledge.cta.subtitle': 'Lassen Sie uns gemeinsam Ihre KI-Strategie entwickeln und erfolgreich umsetzen.',
    'knowledge.cta.button': 'Beratungstermin vereinbaren',

    // KI-Radar
    'knowledge.radar.badge': 'Neu: KI-Radar',
    'knowledge.radar.title': 'Der neue KI-RADAR & Benchmark für Unternehmen',
    'knowledge.radar.subtitle': 'Fundierte Orientierung im KI-Wettbewerb – faktenbasiert, branchenübergreifend, handlungsorientiert.',
    'knowledge.radar.feature1.title': 'Internationale KI-Vergleichsanalyse',
    'knowledge.radar.feature1.description': 'Halbjährlicher strategischer Überblick über KI-Fortschritt in 15 führenden Ländern mit Fokus auf DACH.',
    'knowledge.radar.feature2.title': 'KI-Benchmark für Ihr Unternehmen',
    'knowledge.radar.feature2.description': 'Tiefgreifende Vergleichsanalyse: Wie gut sind Sie im Vergleich zu Wettbewerbern aufgestellt?',
    'knowledge.radar.feature3.title': 'Handlungsempfehlungen für Entscheider',
    'knowledge.radar.feature3.description': 'Konkrete Empfehlungen zur sofortigen Umsetzung mit höchstem Wirkungsgrad.',
    'knowledge.radar.targetAudience.title': 'Für wen ist der KI-RADAR gedacht?',
    'knowledge.radar.targetAudience.ceo': 'CEOs, CIOs und Vorstände mit Digitalverantwortung',
    'knowledge.radar.targetAudience.strategists': 'Digitalisierungs- und KI-Strateg:innen',
    'knowledge.radar.targetAudience.managers': 'Transformationsmanager:innen (250+ Mitarbeitende)',
    'knowledge.radar.newsletter.title': 'KI-RADAR abonnieren',
    'knowledge.radar.newsletter.subtitle': 'Erhalten Sie den KI-RADAR halbjährlich direkt in Ihr Postfach.',
    'knowledge.radar.newsletter.firstNamePlaceholder': 'Vorname',
    'knowledge.radar.newsletter.lastNamePlaceholder': 'Nachname',
    'knowledge.radar.newsletter.emailPlaceholder': 'Ihre E-Mail-Adresse',
    'knowledge.radar.newsletter.subscribe': 'KI-RADAR abonnieren',
    'knowledge.radar.newsletter.submitting': 'Wird angemeldet...',
    'knowledge.radar.newsletter.successTitle': 'Erfolgreich angemeldet!',
    'knowledge.radar.newsletter.successMessage': 'Sie erhalten den nächsten KI-RADAR direkt in Ihr Postfach.',
    'knowledge.radar.newsletter.errorMessage': 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    'knowledge.radar.newsletter.errors.firstName': 'Vorname ist erforderlich',
    'knowledge.radar.newsletter.errors.lastName': 'Nachname ist erforderlich',
    'knowledge.radar.newsletter.errors.email': 'E-Mail-Adresse ist erforderlich',
    'knowledge.radar.newsletter.errors.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'knowledge.radar.newsletter.privacy': 'Ihre Daten werden vertraulich behandelt. Jederzeit abbestellbar.',
    'knowledge.radar.newsletter.frequency': 'pro Jahr',
    'knowledge.radar.newsletter.countries': 'Länder',

    // AI Regulations Section
    'knowledge.regulations.title': 'KI-Regulierungen im Überblick',
    'knowledge.regulations.subtitle': 'In der sich schnell entwickelnden Welt der Künstlichen Intelligenz (KI) ist es entscheidend, die aktuellen gesetzlichen Rahmenbedingungen zu verstehen. Hier finden Sie wichtige Informationen zu den wichtigsten Regulierungen, die die Entwicklung und den Einsatz von KI betreffen.',
    'knowledge.regulations.disclaimer': 'Hinweis: Die hier bereitgestellten Informationen dienen nur zu allgemeinen Informationszwecken und stellen keine Rechtsberatung dar. Es wird keine Haftung für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte übernommen.',
    
    // EU AI Act
    'knowledge.regulations.euaiact.title': 'EU Artificial Intelligence Act',
    'knowledge.regulations.euaiact.what.title': 'Was ist der EU AI Act?',
    'knowledge.regulations.euaiact.what.description': 'Der EU AI Act ist ein Gesetzesvorschlag der Europäischen Union, der darauf abzielt, die Verwendung von KI-Systemen sicher, transparent und ethisch vertretbar zu gestalten. Er teilt KI-Anwendungen in verschiedene Risikoklassen ein und legt spezifische Anforderungen für jede Klasse fest. Ziel ist es, das Vertrauen in KI-Technologien zu stärken und gleichzeitig Innovationen zu fördern.',
    'knowledge.regulations.euaiact.benefit1': 'Fördert die Sicherheit und Zuverlässigkeit von KI-Systemen',
    'knowledge.regulations.euaiact.benefit2': 'Schützt die Grundrechte der Bürger',
    'knowledge.regulations.euaiact.benefit3': 'Schafft einen einheitlichen Rechtsrahmen in der EU',
    'knowledge.regulations.euaiact.benefit4': 'Unterstützt Innovation und Wettbewerbsfähigkeit in der EU',
    'knowledge.regulations.euaiact.benefit5': 'Bietet klare Regeln für Unternehmen, die KI entwickeln und einsetzen',
    'knowledge.regulations.euaiact.link': 'EU AI Act auf DE',
    'knowledge.regulations.euaiact.status': 'Status: In Kraft seit August 2024',
    
    // GDPR
    'knowledge.regulations.gdpr.title': 'Die Datenschutz-Grundverordnung (DSGVO)',
    'knowledge.regulations.gdpr.what.title': 'Was ist die DSGVO?',
    'knowledge.regulations.gdpr.why.title': 'Warum ist die DSGVO wichtig für KI?',
    'knowledge.regulations.gdpr.description': 'Die Datenschutz-Grundverordnung (DSGVO) der EU regelt den Schutz personenbezogener Daten und stärkt die Rechte der betroffenen Personen. Sie legt strenge Anforderungen an die Verarbeitung personenbezogener Daten fest, um die Privatsphäre der Bürger zu schützen. Die DSGVO gilt für alle Unternehmen, die personenbezogene Daten von EU-Bürgern verarbeiten, unabhängig vom Standort des Unternehmens.',
    'knowledge.regulations.gdpr.benefit1': 'Sicherstellung der Transparenz und Fairness bei der Datenverarbeitung',
    'knowledge.regulations.gdpr.benefit2': 'Schutz der Privatsphäre und der personenbezogenen Daten der Nutzer',
    'knowledge.regulations.gdpr.benefit3': 'Stärkung des Vertrauens in KI-Systeme',
    'knowledge.regulations.gdpr.benefit4': 'Verbesserung der Datenkontrolle für Einzelpersonen',
    'knowledge.regulations.gdpr.benefit5': 'Förderung von verantwortungsvollen Datenpraktiken bei Unternehmen',
    'knowledge.regulations.gdpr.link': 'Zur DSGVO',
    'knowledge.regulations.gdpr.status': 'Status: In Kraft seit Mai 2018',
    
    // NIS2
    'knowledge.regulations.nis2.title': 'Die EU-Richtlinie zur Netzwerk- und Informationssicherheit (NIS2)',
    'knowledge.regulations.nis2.what.title': 'Was ist NIS2?',
    'knowledge.regulations.nis2.why.title': 'Warum ist NIS2 wichtig für KI?',
    'knowledge.regulations.nis2.description': 'Die NIS2-Richtlinie ist die überarbeitete Fassung der ursprünglichen NIS-Richtlinie und zielt darauf ab, die Cybersicherheit in der EU zu stärken. Sie erweitert den Anwendungsbereich und verschärft die Anforderungen an das Risikomanagement und die Meldepflichten. NIS2 soll sicherstellen, dass alle relevanten Akteure in der EU robuste Sicherheitsvorkehrungen treffen und aufrechterhalten.',
    'knowledge.regulations.nis2.benefit1': 'Erhöhung der Cybersicherheit für KI-Systeme, die kritische Infrastrukturen betreiben oder unterstützen',
    'knowledge.regulations.nis2.benefit2': 'Einführung strengerer Meldepflichten für Sicherheitsvorfälle',
    'knowledge.regulations.nis2.benefit3': 'Verbesserung der Risikobewältigungsstrategien',
    'knowledge.regulations.nis2.benefit4': 'Erweiterung des Anwendungsbereichs auf mehr Sektoren und Unternehmen',
    'knowledge.regulations.nis2.benefit5': 'Stärkung der Zusammenarbeit und des Informationsaustauschs zwischen den Mitgliedstaaten',
    'knowledge.regulations.nis2.link': 'Zur NIS2',
    'knowledge.regulations.nis2.status': 'Status: In Kraft seit Januar 2023',

    // AI Use Cases / KI-Fallstudien Page
    'usecases.hero.title': 'KI-Fallstudien aus der Praxis',
    'usecases.hero.subtitle': 'Entdecken Sie erfolgreiche KI-Implementierungen aus verschiedenen Branchen und erfahren Sie, wie Unternehmen Künstliche Intelligenz gewinnbringend einsetzen.',
    
    // AICaseHub Section
    'usecases.aicasehub.badge': 'Spin-Off Projekt',
    'usecases.aicasehub.title': 'AICaseHub.io',
    'usecases.aicasehub.subtitle': 'Die umfassende Plattform für KI Use Cases aus allen Branchen',
    'usecases.aicasehub.description': 'AICaseHub.io ist unser Spin-Off Projekt – eine einzigartige Sammlung von KI Use Cases nicht nur von BROVKO, sondern auch von anderen KI-Profis: von großen Beratungen und Software-Herstellern bis hin zu lokalen Agenturen und Anbietern.',
    'usecases.aicasehub.benefit1': 'Große Vielfalt an Use Cases aus diversen Branchen',
    'usecases.aicasehub.benefit2': 'Detaillierte Einblicke in Technologie, Risiken und Budget',
    'usecases.aicasehub.benefit3': 'Projektsteckbriefe zum Download verfügbar',
    'usecases.aicasehub.benefit4': 'Direkte Kontaktaufnahme zu den Anbietern möglich',
    'usecases.aicasehub.cta': 'Jetzt bei AICaseHub.io anmelden',
    'usecases.aicasehub.newsletter': 'Newsletter abonnieren',
    'usecases.aicasehub.newsletter.description': 'Erhalten Sie Updates über neue Use Cases und Entwicklungen bei AICaseHub.io',
    
    // Newsletter Form
    'usecases.newsletter.title': 'Bleiben Sie auf dem Laufenden',
    'usecases.newsletter.subtitle': 'Abonnieren Sie unseren Newsletter und erhalten Sie regelmäßig Updates zu KI-Trends, Best Practices und Erfolgsgeschichten.',
    'usecases.newsletter.firstName': 'Vorname',
    'usecases.newsletter.lastName': 'Nachname',
    'usecases.newsletter.email': 'E-Mail-Adresse',
    'usecases.newsletter.subscribe': 'Newsletter abonnieren',
    'usecases.newsletter.privacy': 'Mit der Anmeldung akzeptieren Sie unsere Datenschutzbestimmungen.',
    'usecases.newsletter.success.title': 'Erfolgreich angemeldet!',
    'usecases.newsletter.success.message': 'Vielen Dank für Ihre Anmeldung. Sie erhalten in Kürze eine Bestätigungs-E-Mail.',
    'usecases.newsletter.submitting': 'Wird gesendet...',

    // Spin-Offs Page
    'spinoffs.hero.title': 'Unsere Spin-Offs',
    'spinoffs.hero.subtitle': 'Von der Idee zum Marktführer: Wir inkubieren innovative Lösungen, entwickeln sie zu marktreifen Produkten und führen sie erfolgreich in den Markt ein. Unser Anspruch: Bei KI-Themen nicht nur vorne dabei zu sein, sondern auch den Markt zu verstehen und die Umsetzungsschlagkraft zu haben.',
    'spinoffs.intro.title': 'Unsere Start-Up Inkubation',
    'spinoffs.intro.description': 'Aus unserer langjährigen Erfahrung in der KI-Beratung identifizieren wir vielversprechende Marktchancen, inkubieren neue Ideen und entwickeln sie systematisch zu skalierbaren Produkten. Durch intensive Marktanalysen und unsere Umsetzungsexpertise entstehen innovative Start-Ups, die spezifische Marktlücken schließen.',
    'spinoffs.process.title': 'Unser Inkubations-Prozess',
    'spinoffs.process.step1': 'Ideen-Inkubation',
    'spinoffs.process.step1.desc': 'Identifikation und Entwicklung innovativer KI-Lösungsansätze',
    'spinoffs.process.step2': 'Produkt-Entwicklung', 
    'spinoffs.process.step2.desc': 'Transformation von Ideen zu marktreifen Produkten',
    'spinoffs.process.step3': 'Skalierung',
    'spinoffs.process.step3.desc': 'Aufbau skalierbarer Geschäftsmodelle und Infrastrukturen',
    'spinoffs.process.step4': 'Markteinführung',
    'spinoffs.process.step4.desc': 'Erfolgreiche Positionierung und Wachstum am Markt',
    'spinoffs.current.title': 'Unsere aktuellen Start-Ups',
    'spinoffs.current.subtitle': 'Diese innovativen Unternehmen sind aus unserem Inkubations-Prozess hervorgegangen und erobern bereits ihre jeweiligen Märkte.',
    
    // AIWizz
    'spinoffs.aiwizz.badge': 'KI-Enterprise Lösung',
    'spinoffs.aiwizz.title': 'AIWizz',
    'spinoffs.aiwizz.subtitle': 'Private LLM-Plattform für Unternehmen',
    'spinoffs.aiwizz.description': 'AIWizz ist mehr als nur ein privates Unternehmens-GPT. Es ist eine orchestrierende, rollenbasierte KI-Plattform, die es Unternehmen ermöglicht, die neuesten LLM-Modelle zu nutzen, während alle Daten sicher im Unternehmen bleiben.',
    'spinoffs.aiwizz.feature1': '100% DSGVO-konforme Private LLM',
    'spinoffs.aiwizz.feature2': 'Multi-Agenten-Systeme für komplexe Prozesse',
    'spinoffs.aiwizz.feature3': 'Systemübergreifende Integration (CRM, ERP, CMS)',
    'spinoffs.aiwizz.feature4': 'VoiceWizz - Intelligente Sprachsteuerung',
    'spinoffs.aiwizz.feature5': 'Rollenbasierte Steuerung und Workflows',
    'spinoffs.aiwizz.cta': 'Mehr über AIWizz erfahren',
    
    // AICaseHub
    'spinoffs.aicasehub.badge': 'Use Case Plattform',
    'spinoffs.aicasehub.title': 'AICaseHub.io',
    'spinoffs.aicasehub.subtitle': 'Die umfassende KI Use Case Plattform',
    'spinoffs.aicasehub.description': 'Eine einzigartige Sammlung von KI Use Cases - nicht nur von BROVKO, sondern auch von anderen KI-Profis: von großen Beratungen bis hin zu lokalen Agenturen. Unternehmen finden hier konkrete Inspiration und detaillierte Umsetzungshilfen.',
    'spinoffs.aicasehub.feature1': 'Große Vielfalt an Use Cases aus diversen Branchen',
    'spinoffs.aicasehub.feature2': 'Detaillierte Technologie- und Budget-Einblicke',
    'spinoffs.aicasehub.feature3': 'Projektsteckbriefe zum Download',
    'spinoffs.aicasehub.feature4': 'Direkte Kontaktaufnahme zu Anbietern',
    'spinoffs.aicasehub.feature5': 'Inspiration für eigene KI-Projekte',
    'spinoffs.aicasehub.cta': 'AICaseHub.io besuchen',
    
    // Investor Section
    'spinoffs.investors.title': 'Anfragen von Investoren',
    'spinoffs.investors.subtitle': 'Sie sind interessiert an Investitionsmöglichkeiten in unsere innovativen Spin-Offs? Kontaktieren Sie uns für detaillierte Informationen und Geschäftsmöglichkeiten.',
    'spinoffs.investors.firstName': 'Vorname',
    'spinoffs.investors.lastName': 'Nachname',
    'spinoffs.investors.email': 'E-Mail-Adresse',
    'spinoffs.investors.company': 'Firma',
    'spinoffs.investors.phone': 'Telefonnummer',
    'spinoffs.investors.submit': 'Investorenanfrage senden',
    'spinoffs.investors.privacy': 'Mit dem Absenden akzeptieren Sie unsere Datenschutzbestimmungen.',
    'spinoffs.investors.success.title': 'Anfrage erfolgreich gesendet!',
    'spinoffs.investors.success.message': 'Vielen Dank für Ihr Interesse. Wir werden uns in Kürze mit detaillierten Informationen bei Ihnen melden.',
    'spinoffs.investors.submitting': 'Wird gesendet...',
    
    // Industries
    'usecases.industries.title': 'Branchen im Fokus',
    'usecases.industries.subtitle': 'KI-Lösungen für alle Branchen und Unternehmensbereiche',
    'usecases.industries.retail': 'Handel',
    'usecases.industries.manufacturing': 'Produktion',
    'usecases.industries.banking': 'Banken',
    'usecases.industries.insurance': 'Versicherungen',
    'usecases.industries.pe': 'Private Equity',
    'usecases.industries.automotive': 'Automotive',
    'usecases.industries.logistics': 'Logistik',
    'usecases.industries.marketing': 'Marketing',
    'usecases.industries.sales': 'Vertrieb',
    'usecases.industries.it': 'IT',
    'usecases.industries.service': 'Service',
    'usecases.industries.controlling': 'Controlling',
    'usecases.industries.datascience': 'Data Science',
    'usecases.industries.healthcare': 'Gesundheitswesen',
    'usecases.industries.education': 'Bildungswesen',
    
    // BROVKO Case Studies
    'usecases.brovko.title': 'BROVKO Fallstudien',
    'usecases.brovko.subtitle': 'Ausgewählte Erfolgsgeschichten unserer KI-Implementierungen',
    
    // Case Study 1
    'usecases.case1.title': 'KI-gestützter Vertriebsassistent für Großhändler',
    'usecases.case1.industry': 'Großhandel & Logistik',
    'usecases.case1.description': 'Effizientere Kundenbetreuung und schnellere Entscheidungen durch Künstliche Intelligenz',
    'usecases.case1.result1': '35% schnellere Angebotsprozesse',
    'usecases.case1.result2': '20% Umsatzsteigerung',
    'usecases.case1.result3': '50% weniger manuelle Recherchezeiten',
    'usecases.case1.technology': 'Private LLM, DSGVO-konforme Architektur',
    
    // Case Study 2
    'usecases.case2.title': 'KI-gestützter 24/7-Support im Maschinenbau',
    'usecases.case2.industry': 'Maschinenbau & Anlagenbau',
    'usecases.case2.description': 'Internationale Kunden- und Händlerunterstützung durch KI-gesteuerte Assistenten in über 12 Sprachen',
    'usecases.case2.result1': '52% schnellere Support-Antworten',
    'usecases.case2.result2': '24% höhere Erstlösungsquote',
    'usecases.case2.result3': '32% weniger Maschinenstillstände',
    'usecases.case2.technology': 'Private LLM, Mehrsprachige KI, Integration CRM/ERP',
    
    // CTA Section
    'usecases.cta.title': 'Bereit für Ihre eigene KI-Erfolgsgeschichte?',
    'usecases.cta.subtitle': 'Lassen Sie uns gemeinsam Ihr KI-Projekt entwickeln und umsetzen.',
    'usecases.cta.button': 'Kostenlose Beratung vereinbaren',

    // KI-Beratung Page
    'kiBeratung.hero.badge': 'KI-Beratung',
    'kiBeratung.hero.title': 'Maßgeschneiderte KI-Strategien für nachhaltigen Unternehmenserfolg',
    'kiBeratung.hero.description': 'Die erfolgreiche Nutzung von Künstlicher Intelligenz (KI) erfordert eine klare Strategie, gezielte Implementierung und eine nachhaltige Integration in bestehende Geschäftsprozesse. Unsere KI-Beratung hilft Unternehmen, KI gezielt einzusetzen, Wettbewerbsvorteile zu sichern und langfristige Erfolge zu erzielen.',
    'kiBeratung.hero.subtitle': 'Wir begleiten Sie von der strategischen Planung bis zur praktischen Umsetzung – mit fundierter Expertise, praxiserprobten Methoden und KI-gestützten digitalen Checklisten für maximale Effizienz.',
    'kiBeratung.hero.cta': 'Beratungsgespräch buchen',
    
    'kiBeratung.whyBrovko.title': 'Warum KI-Beratung von Brovko?',
    'kiBeratung.services.title': 'Unsere Beratungsleistungen',
    'kiBeratung.additional.title': 'Weitere Beratungsfelder',
    'kiBeratung.industry.title': 'Branchenorientierte KI-Beratung',
    'kiBeratung.industry.subtitle': 'Maßgeschneiderte Lösungen für Ihr Geschäft',
    'kiBeratung.methodology.title': 'Unsere Methodik',
    'kiBeratung.methodology.description': 'Wir bieten ganzheitliche KI-Beratung, die sich nicht nur auf strategische Konzepte beschränkt, sondern praktische, umsetzbare Lösungen liefert:',
    'kiBeratung.cta.title': 'Jetzt Ihre individuelle KI-Beratung starten!',
    'kiBeratung.cta.description': 'Möchten Sie wissen, wie Sie KI optimal in Ihrem Unternehmen einsetzen können? Lassen Sie uns gemeinsam eine maßgeschneiderte KI-Strategie entwickeln und Ihre KI-Transformation erfolgreich umsetzen.',

    // KI-Transformation Page
    'kiTransformation.hero.badge': 'KI-Transformation',
    'kiTransformation.hero.title': 'Ganzheitliche KI-Transformation für nachhaltigen Unternehmenserfolg',
    'kiTransformation.hero.description': 'KI-Transformation bedeutet mehr als nur Technologie-Einführung. Wir begleiten Sie auf dem Weg zu einem KI-getriebenen Unternehmen – von der strategischen Vision bis zur nachhaltigen Verankerung in Ihrer Organisation.',
    'kiTransformation.hero.subtitle': 'Strukturierter 4-Phasen-Ansatz mit bewährten Methoden, messbaren Erfolgen und nachhaltiger Befähigung Ihrer Teams.',
    'kiTransformation.hero.cta': 'Transformation planen',
    
    'kiTransformation.whyBrovko.title': 'Warum KI-Transformation mit Brovko?',
    'kiTransformation.phases.title': 'Unser 4-Phasen Transformationsmodell',
    'kiTransformation.services.title': 'Unsere Transformationsleistungen',
    'kiTransformation.industry.title': 'Branchenspezifische Transformationen',
    'kiTransformation.industry.subtitle': 'Erfolgreiche KI-Transformationen in verschiedenen Industrien',
    'kiTransformation.success.title': 'Erfolgsfaktoren für nachhaltige KI-Transformation',
    'kiTransformation.success.subtitle': 'Diese Faktoren entscheiden über den Erfolg Ihrer KI-Transformation:',
    'kiTransformation.cta.title': 'Bereit für Ihre KI-Transformation?',
    'kiTransformation.cta.description': 'Lassen Sie uns gemeinsam den Grundstein für Ihre erfolgreiche KI-Zukunft legen. Starten Sie mit einem kostenlosen Strategiegespräch.',

    // KI-Coaching Page
    'kiCoaching.hero.badge': 'KI-Coaching & KI-Beirat',
    'kiCoaching.hero.title': 'Sparringspartner für Ihre KI-Transformation – auf Augenhöhe',
    'kiCoaching.hero.description': 'KI ist kein kurzfristiger Trend, sondern ein strategischer Game-Changer. BROVKO begleitet Sie mit einem exklusiven Coaching-Angebot auf C-Level – persönlich, vertraulich und wirkungsorientiert.',
    'kiCoaching.hero.subtitle': 'Unser Ziel: Ihnen helfen, die richtigen Dinge zu tun – und sie dann möglichst effektiv umzusetzen.',
    'kiCoaching.hero.cta': 'Erstgespräch buchen',
    
    'kiCoaching.challenges.title': 'Viele Unternehmen stehen vor der Herausforderung',
    'kiCoaching.challenges.subtitle': 'KI-Führung erfordert strategisches Denken und praktische Umsetzungskompetenz',
    'kiCoaching.whyBrovko.title': 'Warum KI-Coaching mit Brovko?',
    'kiCoaching.services.title': 'Unsere Coaching-Angebote',
    'kiCoaching.benefits.title': 'Ihr Mehrwert',
    'kiCoaching.topics.title': 'Coaching-Schwerpunkte',
    'kiCoaching.audience.title': 'Für wen wir arbeiten',
    'kiCoaching.audience.subtitle': 'Exklusive C-Level Begleitung für strategische KI-Entscheidungen',
    'kiCoaching.cta.title': 'Sprechen Sie uns an!',
    'kiCoaching.cta.description': 'Buchen Sie ein kostenloses und unverbindliches Kennenlerngespräch und erfahren Sie, wie wir Sie bei Ihrer KI-Transformation unterstützen können.',

    // KI-Workshops Page
    'kiWorkshops.hero.badge': 'KI-Workshops',
    'kiWorkshops.hero.title': 'Individuelle KI-Workshops für Ihr Unternehmen – praxisnah und ergebnisorientiert',
    'kiWorkshops.hero.description': 'Die erfolgreiche Einführung von Künstlicher Intelligenz (KI) beginnt mit dem richtigen Wissen. Unsere KI-Workshops bieten Ihnen und Ihrem Team einen schnellen, praxisnahen und strategischen Einstieg in zentrale KI-Themen.',
    'kiWorkshops.hero.subtitle': 'Individuell auf Ihre Bedürfnisse zugeschnitten, kombiniert jeder Workshop Fachwissen mit direkt umsetzbaren Ergebnissen.',
    'kiWorkshops.hero.cta': 'Workshop buchen',
    
    'kiWorkshops.whyBrovko.title': 'Warum KI-Workshops von Brovko?',
    'kiWorkshops.workshops.title': 'Unsere KI-Workshops',
    'kiWorkshops.formats.title': 'Workshop-Formate',
    'kiWorkshops.methodology.title': 'Unsere Methodik',
    'kiWorkshops.methodology.subtitle': 'Unsere Workshops sind mehr als reine Theorie. Wir setzen auf interaktive Formate, echte Unternehmensfälle und KI-gestützte digitale Tools, um nachhaltige Ergebnisse zu erzielen.',
    'kiWorkshops.audience.title': 'Für wen sind unsere Workshops?',
    'kiWorkshops.audience.subtitle': 'Maßgeschneiderte Workshop-Inhalte für verschiedene Zielgruppen',
    'kiWorkshops.cta.title': 'Jetzt Ihren individuellen KI-Workshop buchen!',
    'kiWorkshops.cta.description': 'Jeder Workshop wird auf Ihre spezifischen Anforderungen zugeschnitten. Lassen Sie uns gemeinsam herausfinden, wie KI Ihr Unternehmen voranbringen kann.',

    // KI-Anwendungen Page
    'kiAnwendungen.hero.badge': 'KI-Anwendungen',
    'kiAnwendungen.hero.title': 'Individuelle KI-Lösungen für Ihr Unternehmen – sicher, effizient und leistungsstark',
    'kiAnwendungen.hero.description': 'Künstliche Intelligenz (KI) eröffnet Unternehmen völlig neue Möglichkeiten – von der Automatisierung von Prozessen bis hin zur intelligenten Optimierung von Geschäftsmodellen. Wir unterstützen Sie dabei, maßgeschneiderte KI-Anwendungen zu entwickeln, die genau auf Ihre Anforderungen zugeschnitten sind.',
    'kiAnwendungen.hero.subtitle': 'Ob Private LLMs, KI-gestützte Unternehmensplattformen oder smarte Erweiterungen für bestehende Systeme – unsere Lösungen steigern Ihre Effizienz, verbessern Ihre Wettbewerbsfähigkeit und erfüllen höchste Datenschutz- und Compliance-Standards.',
    'kiAnwendungen.hero.cta': 'KI-Projekt starten - Gespräch buchen',
    
    'kiAnwendungen.whyBrovko.title': 'Warum KI-Anwendungen mit Brovko?',
    'kiAnwendungen.applications.title': 'Unsere KI-Lösungen',
    'kiAnwendungen.useCases.title': 'KI-Anwendungen in der Praxis',
    'kiAnwendungen.methodology.title': 'Unsere Methodik – KI passgenau entwickeln & integrieren',
    'kiAnwendungen.methodology.subtitle': 'Unsere KI-Experten begleiten Sie von der ersten Idee bis zur fertigen Implementierung',
    'kiAnwendungen.security.title': 'Höchste Sicherheitsstandards',
    'kiAnwendungen.security.subtitle': 'Unternehmensdaten bleiben sicher und geschützt',
    'kiAnwendungen.cta.title': 'Jetzt Ihr individuelles KI-Projekt starten!',
    'kiAnwendungen.cta.description': 'Möchten Sie wissen, wie Sie KI gezielt in Ihrem Unternehmen einsetzen können? Lassen Sie uns gemeinsam eine maßgeschneiderte KI-Lösung entwickeln – sicher, leistungsstark und zukunftssicher.',

    // KI-Plattform (AIWizz) Page
    'kiPlattform.hero.badge': 'KI-Plattform AIWizz',
    'kiPlattform.hero.title': 'Ihr privates GPT für maximale Produktivität – sicher und leistungsstark',
    'kiPlattform.hero.description': 'AIWizz ermöglicht es Unternehmen, ihre eigene private GPT-Umgebung zu betreiben – ohne dass Daten nach außen gelangen. Nutzen Sie die neuesten LLM-Technologien bedenkenlos für Ihr Unternehmen.',
    'kiPlattform.hero.subtitle': 'Von GPT-Assistenten für jeden Mitarbeiter bis zu Multi-Agenten-Systemen – AIWizz macht Ihre Teams produktiver, effektiver und smarter.',
    'kiPlattform.hero.cta': 'AIWizz Demo buchen',
    'kiPlattform.hero.ctaSecondary': 'Zur AIWizz Seite',
    
    'kiPlattform.whyAIWizz.title': 'Warum AIWizz für Ihr Unternehmen?',
    'kiPlattform.features.title': 'Plattform-Features',
    'kiPlattform.useCases.title': 'KI-Anwendungen nach Abteilungen',
    'kiPlattform.models.title': 'Unterstützte LLM-Modelle',
    'kiPlattform.models.subtitle': 'Zugriff auf die besten und neuesten KI-Technologien',
    'kiPlattform.security.title': 'Höchste Sicherheitsstandards',
    'kiPlattform.security.subtitle': 'Ihre Daten bleiben zu 100% in Ihrem Unternehmen',
    'kiPlattform.cta.title': 'Starten Sie mit AIWizz durch!',
    'kiPlattform.cta.description': 'Erleben Sie die Kraft einer privaten KI-Plattform für Ihr Unternehmen. Buchen Sie eine Demo und sehen Sie, wie AIWizz Ihre Produktivität revolutioniert.',

    // AI-optimierte FAQ Sektion
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Alles was Sie über KI Transformation und custom made KI Anwendungen wissen müssen',
    'faq.cta.title': 'Haben Sie noch Fragen?',
    'faq.cta.subtitle': 'Unsere KI-Transformations-Experten sind da, um Ihnen zu helfen',
    'faq.cta.button': 'Kontakt aufnehmen',

    'faq.valueEngine.question': 'Was ist die KI-Value-Engine?',
    'faq.valueEngine.answer': 'Die KI-Value-Engine ist BROVKOs Methodik, um KI-Transformation messbar zu machen. Sie umfasst Portfolio-Steuerung für alle KI-Initiativen, ein Value-Dashboard für CEO-fähiges Reporting mit EBIT-Impact, und einen Operating Rhythm für kontinuierliche Steuerung. So wird KI vom Hoffnungsträger zum steuerbaren Werttreiber.',

    'faq.valueCheck.question': 'Wofür ist der KI-Value-Check?',
    'faq.valueCheck.answer': 'Der KI-Value-Check ist ein kostenloser, 3-minütiger Selbsttest, der Ihren KI-Reifegrad ermittelt. In 7 Fragen analysieren wir Ihre aktuelle Situation bei Pilotprojekten, Messbarkeit, Governance und Budget. Am Ende erhalten Sie eine personalisierte Analyse mit konkreten Handlungsempfehlungen und können direkt ein Executive Briefing anfragen.',

    'faq.kiTransformation.question': 'Was ist KI Transformation?',
    'faq.kiTransformation.answer': 'KI Transformation ist der ganzheitliche Prozess der Integration von Künstlicher Intelligenz in Unternehmen. BROVKO begleitet Sie von der Strategie-Entwicklung bis zur produktiven Anwendung und bietet custom made KI Anwendungen für nachhaltigen Geschäftserfolg.',

    'faq.customAI.question': 'Was sind custom made KI Anwendungen?',
    'faq.customAI.answer': 'Custom made KI Anwendungen sind maßgeschneiderte AI-Lösungen, die speziell für die Bedürfnisse Ihres Unternehmens entwickelt werden. Dazu gehören Private GPT-Modelle, Enterprise AI-Plattformen und DSGVO-konforme AI-Systeme.',

    'faq.aiVsDigital.question': 'Wie unterscheidet sich AI Transformation von digitaler Transformation?',
    'faq.aiVsDigital.answer': 'AI Transformation geht über klassische Digitalisierung hinaus und fokussiert auf intelligente Automatisierung, Datenanalyse und maschinelles Lernen. BROVKO entwickelt nachhaltige AI-Strategien, die messbare Geschäftsergebnisse liefern.',

    'faq.industries.question': 'Welche Branchen profitieren von KI Transformation?',
    'faq.industries.answer': 'Alle Branchen profitieren von KI Transformation: Produktion & Manufacturing (25% weniger Ausfallzeiten), Finanzdienstleistungen (40% schnellere Entscheidungen), Einzelhandel (20% höhere Conversion), Healthcare (50% schnellere Diagnosen).',

    'faq.timeline.question': 'Wie lange dauert eine KI Transformation?',
    'faq.timeline.answer': 'Eine KI Transformation bei BROVKO erfolgt in 4 Phasen: Strategische Ausrichtung, Organisationsentwicklung, Technische Implementierung und Skalierung. Je nach Unternehmensgröße dauert der Prozess 3-12 Monate für nachhaltige Ergebnisse.',

    'faq.roi.question': 'Welchen ROI kann ich von KI Transformation erwarten?',
    'faq.roi.answer': 'Unsere Kunden sehen typischerweise 20-40% Effizienzsteigerungen im ersten Jahr. Der ROI variiert je nach Branche, umfasst aber Kosteneinsparungen durch Automatisierung, Umsatzsteigerungen durch bessere Entscheidungsfindung und Wettbewerbsvorteile durch custom made KI Anwendungen.',

    // Contact Modal Übersetzungen
    'contact.modal.title': 'Kontakt aufnehmen',
    'contact.modal.subtitle': 'KI-Gespräch buchen',
    'contact.modal.placeholder': 'Ihre Nachricht...',
    'contact.modal.firstName': 'Vorname',
    'contact.modal.lastName': 'Nachname',
    'contact.modal.email': 'E-Mail',
    'contact.modal.phone': 'Telefonnummer',
    'contact.modal.message': 'Ihre Nachricht',
    'contact.modal.cancel': 'Abbrechen',
    'contact.modal.submit': 'Nachricht senden',
    'contact.modal.submitting': 'Wird gesendet...',
    'contact.modal.success.title': 'Nachricht erfolgreich gesendet!',
    'contact.modal.success.text': 'Vielen Dank für Ihr Interesse. Wir melden uns schnell bei Ihnen.',
    'contact.modal.error.title': 'Fehler beim Senden',
    'contact.modal.error.text': 'Entschuldigung, beim Senden der Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    'contact.modal.error.firstName': 'Vorname ist erforderlich',
    'contact.modal.error.lastName': 'Nachname ist erforderlich',
    'contact.modal.error.email': 'Gültige E-Mail-Adresse ist erforderlich',

    // KI-MVP Development Page
    'mvp.seo.title': 'KI-MVP-Entwicklung in 3-4 Wochen | 19.000 EUR Festpreis | BROVKO',
    'mvp.seo.description': 'Ihr funktionierendes KI-MVP in nur 3-4 Wochen zum Festpreis von 19.000 EUR. Schneller Markteintritt mit greifbaren Ergebnissen und minimiertem Risiko.',
    'mvp.seo.keywords': 'KI-MVP, MVP-Entwicklung, KI-Prototyp, AI-MVP, Proof of Concept, KI-Entwicklung, Festpreis, 4 Wochen',

    'mvp.hero.badge': 'Schnelle KI-Umsetzung',
    'mvp.hero.title': 'KI-MVP in 3–4 Wochen',
    'mvp.hero.subtitle': 'Ein funktionierendes KI-MVP zum Festpreis – greifbare Ergebnisse statt langwieriger Projekte. Testen Sie Ihre KI-Idee schnell und risikoarm.',
    'mvp.hero.timeline': '3–4 Wochen Entwicklungszeit',
    'mvp.hero.cta': 'Jetzt MVP-Projekt starten',

    // KI-Lab Page
    'kiLab.seo.title': 'Externes KI-Lab | Sofort einsatzbereit ohne eigenes Team | BROVKO',
    'kiLab.seo.description': 'Ihr externes KI-Lab - von der ersten KI-Idee bis zur eigenen KI-Kompetenz in 4 durchdachten Phasen. Sofort verfügbar, planbare Kosten, echte Ergebnisse.',
    'kiLab.seo.keywords': 'Externes KI-Lab, KI-Team, KI-Outsourcing, KI-Partnerschaft, KI-Souveränität, KI-Kompetenzaufbau',

    'kiLab.hero.title': 'Ihr externes KI-Lab – Sofort einsatzbereit, ohne eigenes Team',
    'kiLab.hero.subtitle': 'Von der ersten KI-Idee bis zur eigenen KI-Kompetenz – in 4 durchdachten Phasen',
    'kiLab.hero.problem': 'Warum 6-12 Monate warten, bis Ihr KI-Team steht? Starten Sie heute mit einem erfahrenen externen KI-Lab.',
    'kiLab.hero.cta': '30-minütiges Gespräch vereinbaren',

    'kiLab.problem.title': 'Das Problem eigener KI-Labs',
    'kiLab.problem.point1': 'Rekrutierung dauert Monate, Aufbau kostet ein Vermögen',
    'kiLab.problem.point2': 'Erste Projekte verzögern sich entsprechend',
    'kiLab.problem.point3': 'Unkalkulierbare Gehälter für KI-Spezialisten',

    'kiLab.solution.title': 'Unsere Lösung: Brovko als Ihr externes KI-Lab',
    'kiLab.solution.description': 'Das Konzept ist einfach: Sie bekommen sofort Zugang zu erfahrenen KI-Entwicklern – ohne Rekrutierung. Wir setzen Ihre Projekte um – schnell, zu fixen Preisen, mit echten Ergebnissen.',
    'kiLab.solution.highlight': 'Strategische Partnerschaft mit klarem Ziel: Ihre KI-Souveränität.',

    'kiLab.phases.title': '4-Phasen-Modell: Ihr Weg zur KI-Souveränität',
    'kiLab.phases.subtitle': 'Von der ersten Idee bis zur kompletten Eigenständigkeit',

    'kiLab.phases.phase1.title': 'Phase 1: Schneller Start',
    'kiLab.phases.phase1.description': 'Sie haben ein konkretes Problem oder eine Idee? Wir liefern Ihnen in 3–4 Wochen einen funktionierenden MVP.',
    'kiLab.phases.phase1.duration': '3–4 Wochen',

    'kiLab.phases.phase2.title': 'Phase 2: Kontinuierliche Entwicklung',
    'kiLab.phases.phase2.description': 'Wir arbeiten als Ihr ausgelagertes KI-Lab: regelmäßige Sprints, feste Kapazitäten, planbare Kosten.',
    'kiLab.phases.phase2.duration': 'Fortlaufend',

    'kiLab.phases.phase3.title': 'Phase 3: Know-how-Transfer',
    'kiLab.phases.phase3.description': 'Wir schulen Ihre Mitarbeiter, dokumentieren unsere Prozesse und helfen beim schrittweisen Kompetenzaufbau.',
    'kiLab.phases.phase3.optional': 'Optional',

    'kiLab.phases.phase4.title': 'Phase 4: Ihr eigenes KI-Lab',
    'kiLab.phases.phase4.description': 'Sie übernehmen komplett. Wir haben Sie so weit gebracht, dass Sie eigenständig arbeiten können.',
    'kiLab.phases.phase4.result': 'KI-Souveränität erreicht',

    'kiLab.benefits.title': 'Warum das funktioniert',
    'kiLab.benefits.benefit1.title': '6–12 Monate Vorlaufzeit sparen',
    'kiLab.benefits.benefit1.description': 'Sofortiger Start ohne langwierigen Team-Aufbau',
    'kiLab.benefits.benefit2.title': 'Fixe Kosten statt unkalkulierbare Gehälter',
    'kiLab.benefits.benefit2.description': 'Planbare Budgets für KI-Spezialisten',
    'kiLab.benefits.benefit3.title': 'Sofortige Ergebnisse statt Anlaufphasen',
    'kiLab.benefits.benefit3.description': 'Funktionsfähige Lösungen von Tag 1',
    'kiLab.benefits.benefit4.title': 'Flexibler Ausstieg – keine Abhängigkeit',
    'kiLab.benefits.benefit4.description': 'Sie bleiben nicht abhängig von uns',

    'kiLab.differentiator.title': 'Das ist kein klassisches Outsourcing',
    'kiLab.differentiator.description': 'Es ist eine strategische Partnerschaft mit klarem Ziel: Ihre KI-Souveränität.',
    'kiLab.differentiator.not.title': 'Kein Outsourcing',
    'kiLab.differentiator.not.description': 'Langfristige Abhängigkeit und Standard-Lösungen',
    'kiLab.differentiator.is.title': 'Strategische Partnerschaft',
    'kiLab.differentiator.is.description': 'Wissenstransfer und Aufbau eigener Kompetenzen',

    'kiLab.audience.title': 'Für wen ist das gedacht?',
    'kiLab.audience.point1': 'Sie wollen mehrere KI-Projekte umsetzen, nicht nur ein MVP',
    'kiLab.audience.point2': 'Sie planen mittelfristig ein eigenes KI-Team, wollen aber jetzt schon starten',
    'kiLab.audience.point3': 'Sie brauchen verlässliche Kapazitäten ohne langes Recruiting',
    'kiLab.audience.point4': 'Sie wollen lernen, während Sie bauen',

    'kiLab.cta.title': 'Interesse? Lassen Sie uns sprechen.',
    'kiLab.cta.description': 'Ein 30-minütiges Gespräch reicht, um herauszufinden, ob dieses Modell zu Ihnen passt.',
    'kiLab.cta.email.title': 'Nachricht senden',
    'kiLab.cta.email.description': 'Beschreiben Sie Ihr Anliegen',
    'kiLab.cta.email.button': 'Nachricht senden',
    'kiLab.cta.mvpLink.text': 'Mehr Infos über MVP von KI-Applikationen:',
    'kiLab.cta.mvpLink.linkText': 'KI-MVP-Entwicklung',
    'kiLab.cta.mainButton': 'Jetzt KI-Lab-Gespräch vereinbaren',

    // Private Equity Whitepaper Page
    'pe.seo.title': 'KI White Paper Serie für Private Equity | Werttreiber & Value Creation | BROVKO',
    'pe.seo.description': 'Exklusive White Paper Serie für PE-Firmen: KI als Werttreiber von Deal Sourcing bis Exit. Kostensenkung, Umsatzsteigerung, Cash-Optimierung & Buy & Build.',
    'pe.seo.keywords': 'Private Equity KI, PE White Paper, KI Value Creation, PE Portfolio Optimierung, AI Private Equity, Buy and Build KI',

    'pe.hero.badge': 'Exklusiv für Private Equity',
    'pe.hero.title': 'KI als Werttreiber für Private Equity',
    'pe.hero.subtitle': 'Exklusive White Paper Serie: Die 4 entscheidenden KI-Hebel für Portfolio-Wertsteigerung und Exit Readiness',
    'pe.hero.benefit1': 'Schnelle ROI-Realisierung',
    'pe.hero.benefit2': 'Exit-relevante Dokumentation',
    'pe.hero.benefit3': 'Portfolio-übergreifende Skalierung',

    'pe.problem.title': 'Private Equity unter Druck',
    'pe.problem.description': 'Klassische Wertsteigerungs-Hebel stoßen an ihre Grenzen. KI eröffnet völlig neue Möglichkeiten für die gesamte PE-Value-Chain.',

    'pe.problem.challenges.title': 'Aktuelle Herausforderungen',
    'pe.problem.challenges.point1': 'Steigende Kaufpreise bei zunehmendem Wettbewerb',
    'pe.problem.challenges.point2': 'Klassische Hebel erreichen ihre Grenzen',
    'pe.problem.challenges.point3': 'Druck auf schnelle, signifikante Wertsteigerung',

    'pe.problem.solution.title': 'KI als Game Changer',
    'pe.problem.solution.description': 'Künstliche Intelligenz revolutioniert die PE-Value-Chain:',
    'pe.problem.solution.point1': 'Deal Sourcing & Due Diligence Beschleunigung',
    'pe.problem.solution.point2': 'Portfolio-Steuerung mit präzisen Forecasts',
    'pe.problem.solution.point3': 'Exit Readiness durch dokumentierte KI-Capabilities',

    'pe.selection.title': 'Wählen Sie Ihre White Paper aus',
    'pe.selection.subtitle': 'Die 4 zentralen KI-Hebel für Private Equity - neu interpretiert durch Künstliche Intelligenz',

    'pe.whitepaper.cost.title': 'Kostensenkung mit KI',
    'pe.whitepaper.cost.description': 'Effizienzsteigerung, Prozessautomatisierung und Predictive Maintenance für Portfolio-Unternehmen',

    'pe.whitepaper.revenue.title': 'Umsatzsteigerung mit KI',
    'pe.whitepaper.revenue.description': 'KI-gestütztes Pricing, Personalisierung und Cross-/Upselling Strategien',

    'pe.whitepaper.cash.title': 'Cash-Optimierung mit KI',
    'pe.whitepaper.cash.description': 'Präzise Forecasts, Working-Capital-Management und intelligente Liquiditätssteuerung',

    'pe.whitepaper.buybuild.title': 'Buy & Build mit KI',
    'pe.whitepaper.buybuild.description': 'Schnelleres Target-Screening, präzisere Synergie-Berechnung und beschleunigte Integration',

    'pe.form.title': 'Jetzt White Paper anfordern',
    'pe.form.subtitle': 'Geben Sie Ihre Kontaktdaten ein und erhalten Sie die ausgewählten White Paper sofort per E-Mail.',
    'pe.form.firstName': 'Vorname',
    'pe.form.lastName': 'Nachname',
    'pe.form.email': 'E-Mail-Adresse',
    'pe.form.phone': 'Telefonnummer (optional)',
    'pe.form.selectedWhitepapers': 'Ausgewählte White Paper',
    'pe.form.submit': 'White Paper anfordern',
    'pe.form.submitting': 'Wird versendet...',
    'pe.form.privacy': 'Ihre Daten werden vertraulich behandelt und ausschließlich für die Zusendung der White Paper verwendet.',

    'pe.form.success.title': 'White Paper werden versendet!',
    'pe.form.success.message': 'Vielen Dank für Ihr Interesse. Die ausgewählten White Paper wurden an Ihre E-Mail-Adresse gesendet.',

    'pe.form.errors.firstName': 'Vorname ist erforderlich',
    'pe.form.errors.lastName': 'Nachname ist erforderlich',
    'pe.form.errors.email': 'E-Mail-Adresse ist erforderlich',
    'pe.form.errors.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'pe.form.errors.whitepapers': 'Bitte wählen Sie mindestens ein White Paper aus',
    'pe.form.errors.submit': 'Fehler beim Senden. Bitte versuchen Sie es erneut.',

    'pe.social.firms': 'PE-Firmen vertrauen uns',
    'pe.social.aum': 'AUM unserer Kunden',
    'pe.social.portfolio': 'Portfolio-Unternehmen',
    'pe.social.testimonial': 'Die KI-Implementierung hat unsere Portfolio-Performance messbar verbessert. BROVKO hat uns geholfen, neue Wertsteigerungs-Potenziale zu identifizieren.',
    'pe.social.testimonialAuthor': '— Managing Partner, Deutsche PE-Firma',

    // KI Organisation Whitepaper Page
    'org.seo.title': 'KI & Mensch im Unternehmen White Papers | Organisation & Arbeitsplatz | BROVKO',
    'org.seo.description': 'White Paper Serie: KI & Organisation, Human in the Loop, KI & Arbeitsplatz. Wie Unternehmen den KI-Wandel erfolgreich gestalten ohne den Menschen zu verlieren.',
    'org.seo.keywords': 'KI Organisation, Human in the Loop, KI Arbeitsplatz, Change Management KI, KI Transformation Unternehmen',

    'org.hero.badge': 'Für Entscheider & Führungskräfte',
    'org.hero.title': 'KI & Mensch im Unternehmen',
    'org.hero.subtitle': 'White Paper Serie: Wie Sie Ihre Organisation auf die KI-Ära ausrichten – ohne den Menschen zu verlieren',
    'org.hero.benefit1': 'CEO-Handlungsrahmen',
    'org.hero.benefit2': 'Neue Arbeitsteilung',
    'org.hero.benefit3': 'Arbeitsplatz der Zukunft',

    'org.problem.title': 'KI verändert alles – aber wie organisieren?',
    'org.problem.description': 'Künstliche Intelligenz verändert Wertschöpfung, Zusammenarbeit und Rollen schneller als jede Technologie zuvor. Viele Unternehmen stehen vor der Frage: Wie organisiere ich diesen Wandel?',

    'org.problem.challenges.title': 'Die Herausforderung',
    'org.problem.challenges.point1': 'KI verändert Rollen und Zusammenarbeit grundlegend',
    'org.problem.challenges.point2': 'Mitarbeiter haben Ängste vor Jobverlust',
    'org.problem.challenges.point3': 'Führung weiß oft nicht, wo sie anfangen soll',

    'org.problem.solution.title': 'Der Lösungsweg',
    'org.problem.solution.description': 'Unsere White Paper Serie zeigt den Weg:',
    'org.problem.solution.point1': 'CEO-Handlungsrahmen für KI-Transformation',
    'org.problem.solution.point2': 'Neue Arbeitsteilung zwischen Mensch und KI',
    'org.problem.solution.point3': 'Arbeitsplatz-Evolution statt Revolution',

    'org.selection.title': 'Wählen Sie Ihre White Paper aus',
    'org.selection.subtitle': 'Drei Perspektiven auf KI & Mensch im Unternehmen – von der Führungsebene bis zum Arbeitsplatz',

    'org.whitepaper.organisation.title': 'KI & Organisation – Warum CEOs handeln müssen',
    'org.whitepaper.organisation.description': 'Strategischer Handlungsrahmen für Führungskräfte: Wie Sie die KI-Transformation in Ihrer Organisation erfolgreich steuern',

    'org.whitepaper.humanloop.title': 'Human in the Loop – Neue Arbeitsteilung zwischen Mensch und KI',
    'org.whitepaper.humanloop.description': 'Praktische Ansätze für die optimale Zusammenarbeit: Wo Menschen unverzichtbar bleiben, wo KI übernimmt',

    'org.whitepaper.workplace.title': 'KI & der Arbeitsplatz – Wie sich jeder Job verändert',
    'org.whitepaper.workplace.description': 'Konkrete Auswirkungen auf Arbeitsplätze: Welche Jobs sich wie wandeln und wie Mitarbeiter sich vorbereiten können',

    'org.form.title': 'Jetzt White Paper anfordern',
    'org.form.subtitle': 'Geben Sie Ihre Kontaktdaten ein und erhalten Sie die ausgewählten White Paper sofort per E-Mail.',
    'org.form.firstName': 'Vorname',
    'org.form.lastName': 'Nachname',
    'org.form.email': 'E-Mail-Adresse',
    'org.form.phone': 'Telefonnummer (optional)',
    'org.form.selectedWhitepapers': 'Ausgewählte White Paper',
    'org.form.submit': 'White Paper anfordern',
    'org.form.submitting': 'Wird versendet...',
    'org.form.privacy': 'Ihre Daten werden vertraulich behandelt und ausschließlich für die Zusendung der White Paper verwendet.',

    'org.form.success.title': 'White Paper werden versendet!',
    'org.form.success.message': 'Vielen Dank für Ihr Interesse. Die ausgewählten White Paper wurden an Ihre E-Mail-Adresse gesendet.',

    'org.form.errors.firstName': 'Vorname ist erforderlich',
    'org.form.errors.lastName': 'Nachname ist erforderlich',
    'org.form.errors.email': 'E-Mail-Adresse ist erforderlich',
    'org.form.errors.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'org.form.errors.whitepapers': 'Bitte wählen Sie mindestens ein White Paper aus',
    'org.form.errors.submit': 'Fehler beim Senden. Bitte versuchen Sie es erneut.',

    'org.social.companies': 'Unternehmen vertrauen uns',
    'org.social.success': 'Erfolgsrate',
    'org.social.leader': 'KI-Vorreiter',
    'org.social.testimonial': 'Die strukturierte Herangehensweise hat uns geholfen, KI erfolgreich zu implementieren ohne unsere Mitarbeiter zu verlieren.',
    'org.social.testimonialAuthor': '— HR Director, DAX-Unternehmen',

    'mvp.whatYouGet.title': 'Was Sie am Ende erhalten',
    'mvp.benefits.testing.title': 'Sofort testbarer Nutzen',
    'mvp.benefits.testing.desc': 'Ein funktionierendes Softwareprodukt, eine Applikation oder App, mit der Sie konkreten Nutzen sofort testen können.',
    'mvp.benefits.acceptance.title': 'User-Akzeptanz prüfen',
    'mvp.benefits.acceptance.desc': 'Testen Sie die Akzeptanz intern und extern – bevor Sie größere Investitionen tätigen.',
    'mvp.benefits.speed.title': 'Zeit & Budget sparen',
    'mvp.benefits.speed.desc': 'Ihre einzige Aufgabe: ein kurzes Projektbriefing mit uns. Wir kümmern uns um den Rest.',

    'mvp.additional.title': 'Zusätzliche Vorteile',
    'mvp.advantages.market.title': 'Schneller Markteintritt',
    'mvp.advantages.market.desc': 'In Wochen statt Monaten am Markt – verschaffen Sie sich einen zeitlichen Vorteil.',
    'mvp.advantages.flexibility.title': 'Maximale Flexibilität',
    'mvp.advantages.flexibility.desc': 'MVPs lassen sich leicht erweitern und anpassen – perfekt für iterative Entwicklung.',
    'mvp.advantages.risk.title': 'Risikominimierung',
    'mvp.advantages.risk.desc': 'Investieren Sie nur in das, was wirklich funktioniert und Nutzen bringt.',
    'mvp.advantages.learning.title': 'Kompetenz aufbauen',
    'mvp.advantages.learning.desc': 'Lernen Sie gleichzeitig, welche KI-Ansätze für Ihr Unternehmen den größten Nutzen stiften.',

    'mvp.bestPart.title': 'Das Beste daran',
    'mvp.bestPart.description': 'Erleben Sie mit uns, wie KI produktiv und sinnvoll eingesetzt werden kann. Sie gewinnen nicht nur ein fertiges Ergebnis, sondern auch Kompetenz im Umgang mit KI.',
    'mvp.bestPart.benefit1': 'Fertiges Ergebnis',
    'mvp.bestPart.benefit2': 'KI-Kompetenz',
    'mvp.bestPart.cta': 'Ihre Idee als MVP umsetzen',

    'mvp.contact.defaultMessage': 'Ich interessiere mich für die KI-MVP-Entwicklung und möchte meine Idee in 3-4 Wochen umsetzen.',

    // Partner Program - DE
    'partner.seo.title': 'Partnerprogramm für IT-Dienstleister | BROVKO',
    'partner.seo.description': 'Sie bleiben Eigentümer Ihrer Kundenbeziehung – wir ergänzen Ihre Leistungen um belastbare KI-Kompetenz.',
    'partner.seo.keywords': 'IT-Dienstleister, KI-Partner, White-Label, Subdienstleister, KI-Kompetenz, Systemhaus',

    'partner.hero.title': 'Partnerprogramm für IT-Dienstleister',
    'partner.hero.subtitle': 'Sie bleiben Eigentümer Ihrer Kundenbeziehung – wir ergänzen Ihre Leistungen um belastbare KI-Kompetenz.',

    'partner.intro.title': 'Was ist das BROVKO-Partnerprogramm?',
    'partner.intro.p1': 'Das BROVKO-Partnerprogramm richtet sich an IT-Dienstleister, die bei ihren Kunden mit konkreten KI-Anfragen konfrontiert werden, diese Themen aber nicht vollständig selbst abdecken wollen.',
    'partner.intro.p2': 'Das Programm ermöglicht es, KI-Projekte gemeinsam umzusetzen, ohne die bestehende Kundenbeziehung zu gefährden oder eigene KI-Teams aufbauen zu müssen.',
    'partner.intro.p3': 'Der IT-Dienstleister bleibt Hauptansprechpartner und Eigentümer der Kundenbeziehung. BROVKO ergänzt im vereinbarten Rahmen die notwendige KI-Kompetenz – von der strategischen Einordnung bis zur operativen Umsetzung.',

    'partner.situation.title': 'Warum dieses Programm existiert',
    'partner.situation.intro': 'Wir sehen bei vielen IT-Dienstleistern aktuell dieselbe Situation:',
    'partner.situation.point1': 'Kunden fragen konkret nach KI (Chatbots, Automatisierung, interne GPTs, Wissenssysteme, Entscheidungsunterstützung)',
    'partner.situation.point2': 'Das Interesse ist real, nicht mehr experimentell',
    'partner.situation.challenge': 'Gleichzeitig ist klar:',
    'partner.situation.challenge1': 'KI ist kein klassisches IT-Thema',
    'partner.situation.challenge2': 'Falsche Versprechen oder halbe Lösungen schaden der Kundenbeziehung',
    'partner.situation.challenge3': 'Eigene KI-Teams aufzubauen ist teuer, riskant und langsam',
    'partner.situation.dilemma': 'Viele IT-Dienstleister stehen deshalb vor einem Dilemma:',
    'partner.situation.dilemmaText': '„Wir wollen unsere Kunden nicht verlieren – aber wir wollen uns auch nicht mit KI verbrennen."',
    'partner.situation.solution': 'Genau hier setzt das BROVKO-Partnerprogramm an.',

    'partner.coreIdea.title': 'Grundidee in einem Satz',
    'partner.coreIdea.text': 'Sie bleiben Eigentümer Ihrer Kundenbeziehung – wir ergänzen Ihre Leistungen um belastbare KI-Kompetenz.',
    'partner.coreIdea.subtitle': 'Nicht mehr. Aber auch nicht weniger.',

    'partner.whoBrovko.title': 'Wer ist BROVKO – und wer wir bewusst nicht sind',
    'partner.whoBrovko.what.title': 'Was wir sind',
    'partner.whoBrovko.what.point1': 'Spezialist für KI-Strategie, KI-Architektur und operative KI-Umsetzung',
    'partner.whoBrovko.what.point2': 'Erfahrung von der strategischen Einordnung bis zum produktiven Betrieb',
    'partner.whoBrovko.what.point3': 'Eigene KI-Produkte: AICaseHub.io (Use-Cases, Struktur, Orientierung) und AIWizz.ai (operative KI-Lösungen)',
    'partner.whoBrovko.whatNot.title': 'Was wir nicht sind',
    'partner.whoBrovko.whatNot.point1': 'Kein klassischer IT-Dienstleister',
    'partner.whoBrovko.whatNot.point2': 'Kein Systemhaus',
    'partner.whoBrovko.whatNot.point3': 'Kein Wettbewerber um Infrastruktur-, Software- oder Betriebsprojekte',
    'partner.whoBrovko.whatNot.point4': 'Kein Akquise-Partner, der Kunden „übernimmt"',
    'partner.whoBrovko.note': 'Das ist wichtig, weil genau hier viele Partnerschaften scheitern.',

    'partner.centralQuestion.title': 'Die zentrale Frage',
    'partner.centralQuestion.question': '„Greifen Sie unsere Kunden ab?"',
    'partner.centralQuestion.answer': 'Nein. Punkt.',
    'partner.centralQuestion.principle': 'Das Partnerprogramm basiert auf einem einfachen Prinzip:',
    'partner.centralQuestion.rule1': 'Keine Direktansprache von Kunden',
    'partner.centralQuestion.rule2': 'Keine Akquise bei bestehenden Kunden',
    'partner.centralQuestion.rule3': 'Kein Umgehen des Partners',
    'partner.centralQuestion.rule4': 'Keine Konkurrenz um Folgeprojekte',
    'partner.centralQuestion.onRequest': 'BROVKO tritt nur auf Wunsch des Partners und nur im vereinbarten Rahmen auf.',
    'partner.centralQuestion.separation': 'Wenn ein Kunde ohne Bezug zum Partner auf uns zukommt, bleibt das unser Kunde. Wenn ein Kunde über den Partner kommt, bleibt er sein Kunde.',
    'partner.centralQuestion.nonnegotiable': 'Diese Trennung ist für uns nicht verhandelbar.',

    'partner.process.title': 'Wie eine Zusammenarbeit konkret aussieht',
    'partner.process.subtitle': 'Typischer Ablauf',
    'partner.process.step1.title': 'Ihr Kunde äußert einen KI-Bedarf',
    'partner.process.step1.example1': 'z. B. „Wir wollen KI im Vertrieb einsetzen"',
    'partner.process.step1.example2': 'oder „Wir brauchen einen internen KI-Assistenten"',
    'partner.process.step2.title': 'Sie entscheiden, ob Sie BROVKO einbinden möchten',
    'partner.process.step2.note1': 'Keine Verpflichtung',
    'partner.process.step2.note2': 'Keine automatische Weitergabe',
    'partner.process.step3.title': 'Gemeinsame Klärung',
    'partner.process.step3.point1': 'Rolle des Partners',
    'partner.process.step3.point2': 'Rolle von BROVKO',
    'partner.process.step3.point3': 'Auftreten gegenüber dem Kunden',
    'partner.process.step4.title': 'Umsetzung',
    'partner.process.step4.point1': 'BROVKO liefert KI-Know-how, Architektur, Umsetzung',
    'partner.process.step4.point2': 'Sie bleiben Hauptansprechpartner',

    'partner.models.title': 'Mögliche Kooperationsmodelle',
    'partner.models.intro': 'Wir halten die Modelle bewusst einfach und flexibel:',
    'partner.models.model1.title': 'Modell 1: Subdienstleister',
    'partner.models.model1.point1': 'Sie sind Prime Contractor',
    'partner.models.model1.point2': 'BROVKO arbeitet im Hintergrund',
    'partner.models.model1.ideal': 'Ideal, wenn Sie maximale Kundensichtbarkeit wollen',
    'partner.models.model2.title': 'Modell 2: Co-Branding',
    'partner.models.model2.point1': 'Gemeinsamer Auftritt',
    'partner.models.model2.point2': '„Powered by BROVKO"',
    'partner.models.model2.ideal': 'Transparent für den Kunden',
    'partner.models.model3.title': 'Modell 3: White-Label',
    'partner.models.model3.point1': 'BROVKO liefert die KI-Leistung',
    'partner.models.model3.point2': 'Sie treten vollständig als Anbieter auf',
    'partner.models.note': 'Das Modell wird pro Projekt entschieden – nicht pauschal.',

    'partner.revenue.title': 'Umsatz & Fairness',
    'partner.revenue.intro': 'Fairness ist für uns kein Marketingbegriff, sondern ein Strukturprinzip.',
    'partner.revenue.point1': 'Transparente Umsatzbeteiligung bei vermittelten oder gemeinsamen Projekten',
    'partner.revenue.point2': 'Klare Vereinbarungen vor Projektstart',
    'partner.revenue.point3': 'Keine versteckten Abhängigkeiten',
    'partner.revenue.point4': 'Keine nachträglichen Änderungen',
    'partner.revenue.goal': 'Ziel ist: Eine Zusammenarbeit, die für beide Seiten wirtschaftlich sinnvoll ist.',

    'partner.benefits.title': 'Was der Partner konkret gewinnt',
    'partner.benefits.point1': 'Erweiterung des Leistungsportfolios ohne eigenes KI-Team',
    'partner.benefits.point2': 'Professionelles Auftreten bei KI-Anfragen',
    'partner.benefits.point3': 'Reduktion von Umsetzungs- und Reputationsrisiken',
    'partner.benefits.point4': 'Zusätzliche Umsätze durch KI-Projekte',
    'partner.benefits.point5': 'Stärkere Kundenbindung durch glaubwürdige Antworten',
    'partner.benefits.quote': '„Wir können KI anbieten, ohne Bauchschmerzen zu haben."',
    'partner.benefits.quoteNote': 'Viele Partner sagen nach den ersten Projekten:',

    'partner.suitability.title': 'Für wen das Programm geeignet ist – und für wen nicht',
    'partner.suitability.suitable.title': 'Geeignet für:',
    'partner.suitability.suitable.point1': 'IT-Dienstleister mit bestehenden Kunden',
    'partner.suitability.suitable.point2': 'Systemhäuser, Integratoren, IT-Berater',
    'partner.suitability.suitable.point3': 'Unternehmen, die langfristige Kundenbeziehungen schützen wollen',
    'partner.suitability.notSuitable.title': 'Nicht geeignet für:',
    'partner.suitability.notSuitable.point1': 'Reine Lead-Sammler',
    'partner.suitability.notSuitable.point2': 'Vermittler ohne Kundenbindung',
    'partner.suitability.notSuitable.point3': 'Partner, die nur kurzfristige Margen suchen',

    'partner.form.title': 'Nächster Schritt',
    'partner.form.subtitle': 'Das Partnerprogramm ist kein Self-Service-Produkt. Der nächste Schritt ist immer: ein unverbindliches Gespräch, um zu prüfen, ob Denkweise, Anspruch und Vorgehen zusammenpassen.',
    'partner.form.note': 'Erst danach entscheiden beide Seiten, ob und wie eine Zusammenarbeit sinnvoll ist.',
    'partner.form.name': 'Name',
    'partner.form.company': 'Unternehmen',
    'partner.form.email': 'E-Mail',
    'partner.form.message': 'Kurze Nachricht',
    'partner.form.messagePlaceholder': 'Optional: Was interessiert Sie besonders?',
    'partner.form.submit': 'Gespräch anfragen',
    'partner.form.submitting': 'Wird gesendet...',
    'partner.form.privacy': 'Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.',
    'partner.form.errors.name': 'Bitte geben Sie Ihren Namen ein',
    'partner.form.errors.company': 'Bitte geben Sie Ihr Unternehmen ein',
    'partner.form.errors.email': 'Bitte geben Sie eine E-Mail-Adresse ein',
    'partner.form.errors.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'partner.form.errors.submit': 'Es gab einen Fehler beim Absenden. Bitte versuchen Sie es erneut.',
    'partner.form.success.title': 'Vielen Dank für Ihre Anfrage',
    'partner.form.success.message': 'Wir melden uns zeitnah bei Ihnen.',
  },
  
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.valueEngine': 'AI Value Engine',
    'nav.services': 'Services',
    'nav.caseStudies': 'Case Studies',
    'nav.knowledgeCenter': 'Knowledge Center',
    'nav.about': 'About Us',
    'nav.clients': 'Clients',
    'nav.spinoffs': 'Our Spin-Offs',
    'nav.products': 'Products',
    'nav.dropdown.allProducts': 'All products',
    'nav.dropdown.granalis': 'Granalis',
    'nav.dropdown.vox2doc': 'Vox2Doc',
    'nav.dropdown.domicus': 'Domicus',
    'nav.dropdown.fleetdoc': 'Fleetdoc',
    'nav.dropdown.fairspark': 'Fairspark',
    'nav.dropdown.replyly': 'Replyly',
    'nav.search': 'Search...',
    'nav.solutions': 'Solutions',
    'nav.enablement': 'Enablement',
    'nav.expertise': 'Expertise',
    'nav.ctaButton': 'Start AI Value Check',
    
    // AI Value Engine Navigation Dropdown
    'nav.dropdown.theSystem': 'The Engine',
    'nav.dropdown.valueCheck': 'AI Value Check',
    'nav.dropdown.valueDashboard': 'Value Dashboard',
    'nav.dropdown.sovereignCloud': 'AI Sovereignty',

    // Solutions Navigation Dropdown
    'nav.dropdown.roiImpact': 'ROI & Business Impact',
    'nav.dropdown.transformation': 'Strategic AI Transformation',
    'nav.dropdown.privateEquity': 'Private Equity',

    // Enablement Navigation Dropdown
    'nav.dropdown.academy': 'AI Academy',
    'nav.dropdown.workshops': 'AI Workshops',
    'nav.dropdown.executiveCoaching': 'AI Executive Coaching',

    // Expertise Navigation Dropdown
    'nav.dropdown.whitepapersStudies': 'Whitepapers & Studies',
    'nav.dropdown.useCases': 'Use Cases',
    'nav.dropdown.aiwizzPlatform': 'AIWizz Platform',

    // Legacy - keep for compatibility
    'nav.dropdown.coaching': 'AI Coaching',
    'nav.dropdown.applications': 'AI Applications',
    'nav.dropdown.platform': 'AI Platform',
    'nav.dropdown.mvp': 'AI-MVP Development',
    'nav.dropdown.kilab': 'External AI Lab',

    // Hero Section
    'hero.badge': 'AI Roadmap in 30 Days',
    'hero.title.part1': 'Your One-Stop Partner for',
    'hero.title.part2': 'AI Transformation',
    'hero.title.part3': '– from strategy to productive application.',
    'hero.subtitle': 'With the unique BROVKO Tool Box and more than 20 top developers',
    'hero.cta': 'Start AI Transformation',

    // Strategy Section
    'strategy.title.part1': 'AI is not a tool topic.',
    'strategy.title.part2': 'It is a transformation topic.',
    'strategy.subtitle': 'In a world where technical AI setups are becoming increasingly simple and affordable, many companies lack what is crucial:',
    'strategy.point1.title': 'Clarity about actual value',
    'strategy.point1.desc': 'We help you identify the real value of AI for your company.',
    'strategy.point2.title': 'Framework for your organization',
    'strategy.point2.desc': 'Structured approach for successful AI implementation.',
    'strategy.point3.title': 'Roadmap for real impact',
    'strategy.point3.desc': 'From technology to measurable business results.',

    // Solutions Section
    'solutions.title': 'Our AI Solutions for Your Company',
    'solutions.subtitle': 'We offer flexible AI solutions tailored to your needs. Choose the right entry point for your company:',
    'solutions.workshops.title': 'AI Transformation',
    'solutions.workshops.desc': 'Strategic transformation and sustainable change.',
    'solutions.consulting.title': 'AI-Academy',
    'solutions.consulting.desc': 'Learn. Apply. Transform.',
    'solutions.applications.title': 'AI Applications',
    'solutions.applications.desc': 'Customized AI projects for your company.',
    'solutions.platforms.title': 'AI Platforms',
    'solutions.platforms.desc': 'Scalable, modular AI systems.',
    'solutions.learnMore': 'Learn more...',

    // Benefits Section
    'benefits.title': 'We bring order, structure and impact to your AI initiatives.',
    'benefits.compass.title': 'AI Compass',
    'benefits.compass.desc': 'Orientation & strategic alignment',
    'benefits.analysis.title': 'Potential Analysis',
    'benefits.analysis.desc': 'Identification & prioritization of impactful use cases',
    'benefits.canvas.title': 'AI Canvas',
    'benefits.canvas.desc': 'Holistic planning and management of your AI activities',
    'benefits.playbook.title': 'Prompt Playbook',
    'benefits.playbook.desc': 'Enablement for employees & executives',
    'benefits.matrix.title': 'Time-Waster Matrix',
    'benefits.matrix.desc': 'Efficiency increase through automation of routine activities',
    'benefits.factory.title': 'Use Case Factory',
    'benefits.factory.desc': 'Proven templates for rapid implementation of 30+ AI use cases',
    'benefits.cta': 'Learn more about our AI success stories →',

    // Achievements
    'achievements.title': 'What you achieve with BROVKO',
    'achievements.clarity.title': 'Clarity about strategy, priorities and impact',
    'achievements.clarity.desc': 'Structured approach for sustainable AI transformation',
    'achievements.speed.title': 'Speed through pre-designed tools & methods',
    'achievements.speed.desc': 'Proven frameworks accelerate your implementation',
    'achievements.impact.title': 'Impact through concrete results – often in less than 90 days',
    'achievements.impact.desc': 'Measurable success in short time through focused implementation',
    'achievements.security.title': 'Security through structured guidance and know-how building',
    'achievements.security.desc': 'Experts with deep AI & industry know-how by your side',

    // Target Audience
    'audience.title': 'Who we work for',
    'audience.clevel.title': 'C-Level & Strategy Units',
    'audience.clevel.desc': 'Roadmaps, management, governance',
    'audience.clevel.details': 'Strategic leadership and governance structures for successful AI transformation at enterprise level.',
    'audience.enterprise.title': 'Mid-market & Large Enterprises',
    'audience.enterprise.desc': 'Readiness, internal use cases, scaling',
    'audience.enterprise.details': 'Comprehensive support from initial analysis to enterprise-wide scaling of AI solutions.',
    'audience.pe.title': 'Private Equity',
    'audience.pe.desc': 'Portfolio enablement, quick wins, exit preparation',
    'audience.pe.details': 'Value creation through strategic AI implementation in portfolio companies for optimal exit scenarios.',

    // Resources
    'resources.subtitle': 'From customized AI solutions to valuable resources – here you will find everything you need for your digital transformation.',
    'resources.expertise.title': 'Our Expertise',
    'resources.expertise.desc': 'Learn how Brovko provides strategic AI consulting and turnkey solutions that take your company to the next level.',
    'resources.caseStudies.title': 'Projects and Case Studies',
    'resources.caseStudies.desc': 'See how we have helped companies in various industries succeed through innovative AI solutions.',
    'resources.whitepapers.title': 'White Papers & Resources',
    'resources.whitepapers.desc': 'Download our latest white papers to dive deeper into the world of AI transformation.',
    'resources.aiwizz.title': 'AIWizz - Our Product Brand',
    'resources.aiwizz.desc': 'Discover our independent brand for turnkey AI solutions, based on years of experience.',
    'resources.books.title': 'Our Books',
    'resources.books.desc': 'Read our book "AI Revolution in Companies" and learn how AI can transform your industry.',
    'resources.coaching.title': 'AI Coaching & AI Advisory',
    'resources.coaching.desc': 'Customized AI coaching for executives who want to successfully lead their organization through digital transformation.',

    // CTA Section
    'cta.title': 'The time for POCs is over.',
    'cta.subtitle': 'Now we need implementation with impact.',
    'cta.description1': 'BROVKO provides clarity and structure in a complex AI world. We connect methods, technology and strategic management – individual, pragmatic, effective.',
    'cta.description2': 'No buzzwords. No overengineering. But: Impact. Also with AIWizz – our immediately deployable AI plugins for support, forecasting, sales and much more.',
    'cta.button': 'Book free initial consultation now',

    // Clients Section
    'clients.title': 'Our Clients',
    'clients.subtitle': 'Selected companies we have successfully collaborated with on various projects.',
    'clients.stat1': '100+ successful projects',
    'clients.stat2': '50+ satisfied clients',
    'clients.stat3': '5+ years AI expertise',

    // Newsletter
    'newsletter.badge': 'Newsletter',
    'newsletter.title': 'Sign up for our newsletter',
    'newsletter.subtitle': 'Stay up to date on the latest AI trends, best practices and success stories from practice.',
    'newsletter.firstName': 'First Name',
    'newsletter.lastName': 'Last Name',
    'newsletter.email': 'Email Address',
    'newsletter.consent': 'I have read the privacy policy and agree to the processing of my data.',
    'newsletter.privacy': 'Privacy Policy',
    'newsletter.subscribe': 'Subscribe',
    'newsletter.submitting': 'Subscribing...',
    'newsletter.success': 'Successfully subscribed!',
    'newsletter.successDesc': 'Thank you for subscribing to our newsletter.',
    'newsletter.error': 'Error subscribing',
    'newsletter.errorDesc': 'Please try again later.',
    'newsletter.fillFields': 'Please fill in all fields',
    'newsletter.fillFieldsDesc': 'All fields are required.',
    'newsletter.consentRequired': 'Privacy policy required',
    'newsletter.consentRequiredDesc': 'Please accept the privacy policy.',
    'newsletter.benefit1': 'Monthly Insights',
    'newsletter.benefit2': 'Exclusive Content',
    'newsletter.benefit3': 'Cancel anytime',

    // ChatBot (removed)
    

    // Footer
    'footer.company': 'Your partner for AI transformation. We bring order, structure and measurable impact to your AI initiatives – from strategy to implementation.',
    'footer.companyName': 'BROVKO GmbH',
    'footer.companyDesc1': 'Strategic AI Consulting',
    'footer.companyDesc2': 'Germany',
    'footer.quickLinks': 'Quick Access',
    'footer.services': 'Services',
    'footer.caseStudies': 'Case Studies',
    'footer.knowledgeCenter': 'Knowledge Center',
    'footer.about': 'About Us',
    'footer.peWhitepaper': 'PE White Papers',
    'footer.orgWhitepaper': 'AI & Organization',
    'footer.partnerProgram': 'Partner Program for IT Service Providers',
    'footer.contact': 'Contact',
    'footer.contactInfo': 'Contact',
    'footer.email': 'email@brovko.de',
    'footer.website': 'www.brovko.de',
    'footer.copyright': `© ${new Date().getFullYear()} Brovko GmbH. All rights reserved.`,
    'footer.imprint': 'Imprint',
    'footer.privacy': 'Privacy',
    'footer.cookieNotice': 'We use cookies on our website to see how you interact with it. By accepting, you agree to our use of such cookies.',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.settings': 'Settings',
    'footer.decline': 'Decline',
    'footer.accept': 'Accept',

    // Services Page
    'services.hero.title': 'Our AI Services',
    'services.hero.subtitle': 'Tailored AI solutions for sustainable business success. From strategic planning to practical implementation.',
    'services.hero.experience': '5+ Years AI Expertise',
    'services.overview.title': 'Our Service Areas',
    'services.overview.subtitle': 'Choose the right entry point for your company. All services are individually adaptable to your needs.',
    
    'services.transformation.title': 'AI Transformation',
    'services.transformation.description': 'Holistic AI transformation for sustainable business results and strategic competitive advantages.',
    'services.transformation.feature1': 'Strategic AI roadmap',
    'services.transformation.feature2': 'Change management',
    'services.transformation.feature3': 'Cultural change',
    'services.transformation.feature4': 'Measurable transformation',
    
    'services.consulting.title': 'AI-Academy',
    'services.consulting.description': 'Comprehensive educational programs and practical training for sustainable AI competency development.',
    'services.consulting.feature1': 'C-Level Coaching',
    'services.consulting.feature2': 'EU AI Act & Governance',
    'services.consulting.feature3': 'Practical Training',
    'services.consulting.feature4': 'Individual AI Courses',
    
    'services.coaching.title': 'AI Coaching & AI Advisory',
    'services.coaching.description': 'Personal guidance at C-level for strategic AI decisions and implementation.',
    'services.coaching.feature1': '1:1 coaching for executives',
    'services.coaching.feature2': 'AI advisory & AI committee',
    'services.coaching.feature3': 'Strategic sparring',
    'services.coaching.feature4': 'Board reporting & KPIs',
    
    'services.workshops.title': 'AI Workshops',
    'services.workshops.description': 'Practical workshops for quick results and directly implementable action recommendations.',
    'services.workshops.feature1': 'AI strategy workshop',
    'services.workshops.feature2': 'AI potential analysis',
    'services.workshops.feature3': 'Live demos & use cases',
    'services.workshops.feature4': 'Digital AI checklists',
    
    'services.platform.title': 'AI Platform',
    'services.platform.description': 'AIWizz - Your private GPT environment for maximum productivity',
    'services.platform.feature1': 'Private GPT without data transmission',
    'services.platform.feature2': 'Latest LLM technologies (GPT, Claude, Gemini)',
    'services.platform.feature3': 'Multi-agent systems for all departments',
    'services.platform.feature4': 'GDPR compliant and highest security',
    
    'services.applications.title': 'AI Applications',
    'services.applications.description': 'Customized AI projects and turnkey solutions for your company.',
    'services.applications.feature1': 'Custom AI development',
    'services.applications.feature2': 'Integration of existing systems',
    'services.applications.feature3': 'Scalable architectures',
    'services.applications.feature4': 'Maintenance & support',

    'services.mvp.title': 'AI-MVP Development',
    'services.mvp.description': 'Your working AI-MVP in just 3-4 weeks at a fixed price. Fast, low-risk implementation of your AI idea.',
    'services.mvp.feature1': 'Fixed price of 19,000 EUR',
    'services.mvp.feature2': 'Delivery in 3-4 weeks',
    'services.mvp.feature3': 'Immediately testable value',
    'services.mvp.feature4': 'Minimized risk',
    'services.mvp.highlight': 'NEW',
    'services.mvp.cta': 'Start MVP now',

    'services.kilab.title': 'External AI Lab',
    'services.kilab.description': 'Your outsourced AI team for continuous development and knowledge transfer until your own AI sovereignty.',
    'services.kilab.feature1': 'Immediately available AI team',
    'services.kilab.feature2': 'Fixed costs instead of salaries',
    'services.kilab.feature3': '4-phase model to independence',
    'services.kilab.feature4': 'Flexible exit possible',
    
    'services.learnMore': 'Learn more',
    
    'services.why.title': 'Why BROVKO?',
    'services.why.subtitle': 'Our approach combines strategic clarity with practical implementation for measurable results.',
    'services.why.expertise.title': 'Expertise',
    'services.why.expertise.description': '5+ years AI experience across various industries',
    'services.why.practical.title': 'Practical',
    'services.why.practical.description': 'Directly implementable solutions instead of theory',
    'services.why.results.title': 'Results-oriented',
    'services.why.results.description': 'Measurable success often in less than 90 days',
    'services.why.support.title': 'Support',
    'services.why.support.description': 'Structured support from start to success',
    
    'services.cta.title': 'Ready for your AI transformation?',
    'services.cta.subtitle': 'Let us develop and successfully implement your AI strategy together.',
    'services.cta.button': 'Book free initial consultation',

    // About Page
    'about.hero.title': 'About BROVKO',
    'about.hero.subtitle': 'Get to know our team, our mission and our vision. Discover how we help companies succeed with customized AI solutions.',
    
    'about.team.title': 'The Brovko Team',
    'about.team.description': 'Our team consists of more than 20 experienced specialists who bring deep expertise in AI projects and transformation processes. Our experts have worked in various industries such as retail, manufacturing, banking and insurance and have extensive experience as CIOs and CDOs. With this know-how, we support you in the successful implementation of customized AI solutions.',
    'about.team.specialists': 'Experienced Specialists',
    'about.team.expertise': 'CIOs, CDOs and AI Experts',
    
    'about.services.title': 'BROVKO Services',
    'about.services.description': 'With over a decade of experience in advising leading companies at national and international levels, we have developed comprehensive competence in digitalization, IT/digitalization strategy and artificial intelligence. Our expertise includes strategy development, training and operational implementation. We have a deep understanding of processes and challenges in marketing, sales, CRM, product management, logistics, production and HR. This combination of IT and AI expertise as well as specialized knowledge makes us the ideal partner for your successful AI transformation.',
    'about.services.button': 'Our AI Services',
    
    'about.mission.title': 'Our Mission',
    'about.mission.description': 'Our mission is to make companies future-proof and competitive with the help of state-of-the-art AI technologies. We believe that AI is the key to sustainable transformation and want to show you how this technology can revolutionize your business.',
    
    'about.vision.title': 'Our Vision',
    'about.vision.description': 'We strive to be a leader in strategic AI consulting to help companies in Germany and internationally fully exploit the opportunities of digital transformation. Our goal is to be your first choice for innovative, strategic AI solutions.',
    
    'about.values.title': 'Our Values',
    'about.values.subtitle': 'These principles guide our actions and shape our collaboration with you.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description': 'We rely on continuous innovation to offer the best and latest AI technologies.',
    'about.values.excellence.title': 'Excellence',
    'about.values.excellence.description': 'Quality and excellence are the foundations of our actions. We always deliver first-class solutions and services.',
    'about.values.customer.title': 'Customer Focus',
    'about.values.customer.description': 'Our customers are at the center of everything we do. We work closely with them to develop customized solutions that meet their individual needs.',
    'about.values.integrity.title': 'Integrity',
    'about.values.integrity.description': 'Honesty and transparency are important to us. We always act ethically and responsibly.',
    'about.values.teamwork.title': 'Teamwork',
    'about.values.teamwork.description': 'We believe in the power of collaboration. Our team of experts works together to achieve the best results.',
    
    'about.history.title': 'Company History',
    'about.history.founding': 'Founded',
    'about.history.description': 'Since our founding in 2011, we have been accompanying companies in almost all business areas and industries on their path to successful digital transformation. With our broad industry expertise, we understand the diverse challenges of our customers and develop individual, innovative solutions that help them remain competitive and achieve sustainable success. We place special emphasis on practice-oriented approaches that deliver measurable results. Our goal is to strengthen companies in the long term and position them optimally for the future.',
    
    'about.resources.title': 'Our Resources',
    'about.resources.subtitle': 'With our headquarters in Hanover, our Business Excellence Center in Düsseldorf, a large, competent development team in Wrocław, Poland, and our AI think tank in Haifa, Israel, we serve both national and international projects. This global structure enables us to develop and implement customized solutions for companies worldwide.',
    'about.locations.germany': 'Germany',
    'about.locations.poland': 'Poland',
    'about.locations.israel': 'Israel',
    'about.locations.hannover.role': 'Headquarters',
    'about.locations.hannover.description': 'Our headquarters in Hanover is the heart of our strategic business development. From here we control the planning and alignment of our company, develop innovative AI strategies and work closely with our customers on implementing their digital transformation. In addition, we coordinate all operational activities from this location to ensure that our projects run smoothly and successfully.',
    'about.locations.dusseldorf.role': 'Business Excellence',
    'about.locations.dusseldorf.description': 'Our location in Düsseldorf is the center for Business Excellence. Here we work to set the highest standards in all business areas and ensure that our customers benefit from first-class strategies and operational excellence. This location plays a central role in implementing our corporate vision to ensure sustainable success and peak performance in all projects.',
    'about.locations.breslau.role': 'Software Development',
    'about.locations.breslau.description': 'Our location in Wrocław, Poland, houses a highly qualified team of software experts, including software developers, data scientists, data engineers and AI specialists. This team works on developing innovative, customized solutions and supports our customers worldwide with advanced technologies and deep expertise.',
    'about.locations.haifa.role': 'AI-Think Tank & Trend Scouting',
    'about.locations.haifa.description': 'Our location in Israel and our partners serve as our AI think tank and trend scouting division, which observes and evaluates current AI developments, anticipates the next steps in AI development, and acts as both an impulse generator and sparring partner in developing new ideas, use cases, and business models.',
    
    'about.cta.title': 'Get to know us',
    'about.cta.subtitle': 'See for yourself our expertise and approach.',
    'about.cta.button': 'Schedule a meeting',

    // Knowledge Center
    'knowledge.hero.title': 'AI Library: Your Knowledge Center for Artificial Intelligence',
    'knowledge.hero.subtitle': 'Discover comprehensive white papers, use cases and case studies to fully exploit the potential of AI in your company.',
    
    'knowledge.books.title': 'Our Current Books',
    'knowledge.books.amazonButton': 'Buy on Amazon',
    'knowledge.books.book1.title': 'AI Revolution in Companies',
    'knowledge.books.book1.subtitle': 'Opportunities - Case Studies - Success Factors',
    'knowledge.books.book1.description': 'Artificial Intelligence (AI) is no longer a vision of the future, but a driving force behind digital transformation in businesses worldwide. This book offers a practical introduction to the numerous potentials and challenges that AI has in store for everyday business, aimed at experienced managers and decision-makers who want to exploit the possibilities of AI in their company.',
    'knowledge.books.book2.title': 'AI Potential Analysis in Companies',
    'knowledge.books.book2.subtitle': 'How companies can use an AI potential analysis to identify opportunities, set priorities, plan implementation and create real added value',
    'knowledge.books.book2.description': 'This book provides a practical and strategically grounded blueprint for organizations aiming to use AI effectively. It is specifically targeted at C-level management, decision-makers, department heads, and AI strategists – and explains step by step how to conduct a professional AI potential analysis.',
    
    'knowledge.whitepapers.title': 'Discover Our White Papers',
    'knowledge.whitepapers.subtitle': 'Download our latest white papers and gain valuable insights into AI strategies and best practices.',
    'knowledge.whitepapers.wp1.title': 'Artificial Intelligence in Companies: Success Factors and Case Studies',
    'knowledge.whitepapers.wp2.title': 'Chief Artificial Intelligence Officer (CAIO) as Architect and Pioneer of AI Transformation',
    'knowledge.whitepapers.wp3.title': 'The CEO or Managing Director as Driver of AI Transformation',
    'knowledge.whitepapers.wp4.title': 'AI Application in the Private Equity and M&A Industry',
    'knowledge.whitepapers.wp5.title': 'Generative AI: The Game Changer for Companies',
    'knowledge.whitepapers.wp6.title': 'Process Intelligence: Process Optimization with Artificial Intelligence (AI)',
    'knowledge.whitepapers.wp7.title': 'Maximum Value Creation through AI: How Private Equity Companies Can Significantly Increase the Value of Their Portfolio Companies',
    'knowledge.whitepapers.wp8.title': 'AI-Supported Forecasts: Precision Redefined – How Artificial Intelligence Revolutionizes Predictions',
    'knowledge.whitepapers.wp9.title': 'AI Application in the Automotive Industry',
    'knowledge.whitepapers.wp10.title': 'Get Started with AI Now: The Concerns from the Current AI Ifo Study and What Solutions Exist',
    'knowledge.whitepapers.wp11.title': 'AI in E-Commerce: How Artificial Intelligence Revolutionizes the Shopping Experience and Efficiency',
    'knowledge.whitepapers.wp12.title': 'The Future of Retail: Success Factor Artificial Intelligence',
    'knowledge.whitepapers.wp13.title': 'Reducing Costs with Artificial Intelligence: A Practical Approach for Companies',
    'knowledge.whitepapers.wp14.title': 'Revenue Increase through AI: Potential, Strategies and Practice-Oriented Approaches',
    'knowledge.whitepapers.wp15.title': 'Data Intelligence: Turning Company Data into Data Gold with AI',
    'knowledge.whitepapers.wp16.title': 'Success through AI: Strategic Innovations in Pricing',
    'knowledge.whitepapers.wp17.title': 'Next-Level Customer Portals with Artificial Intelligence (AI)',
    'knowledge.whitepapers.wp18.title': 'AI Transformation: From AI Strategy to AI Value Champion',
    'knowledge.whitepapers.wp19.title': 'AI Strategy and AI Governance: Foundation for Transformation towards AI Mastery',
    'knowledge.whitepapers.wp20.title': 'AI Technology & Architecture – The Foundation for Scalable AI Applications (Private LLM for Companies)',
    'knowledge.whitepapers.wp21.title': '7 AI Skills You MUST Master – or Fall Behind in the Competition',
    'knowledge.whitepapers.wp22.title': 'Why Start with AI Now?!',
    'knowledge.whitepapers.wp23.title': 'AI Impact After 90/120/150 Days – What to Expect from AI Projects Today',
    'knowledge.whitepapers.wp24.title': 'Company-owned AI as a Central Orchestration Unit for Systems, Processes and Knowledge',
    'knowledge.whitepapers.wp25.title': 'AI in Banking – Education meets Experience',
    'knowledge.whitepapers.wp26.title': 'The Brovko Approach for AI in Private Equity (PE) Companies',
    'knowledge.whitepapers.wp27.title': 'AI in Switzerland – Secure, Concrete, Effective',
    'knowledge.whitepapers.wp28.title': 'Artificial Intelligence in Austria – How to Successfully Implement AI',
    'knowledge.whitepapers.wp29.title': 'Artificial Intelligence in Action – 5 Realistic Applications for Private Equity',
    'knowledge.whitepapers.wp30.title': 'AIWizz – The Enterprise AI Platform for Impact, Security and Scalability',
    'knowledge.whitepapers.wp31.title': 'AI in Sales',
    'knowledge.whitepapers.wp32.title': 'Skills Shortage? Intelligently Unburden with AI – Instead of Replacing Personnel',
    'knowledge.whitepapers.wp33.title': 'AIWizz - The New Control Center for Your Company: Your Own AI. Your Rules. Your Impact.',
    'knowledge.whitepapers.wp34.title': 'AIWizz for Private Equity',
    'knowledge.whitepapers.wp35.title': 'Best Practices in Switzerland',
    'knowledge.whitepapers.wp36.title': 'ChatGPT 5 Changes Everything',
    
    'knowledge.download.title': 'Download White Paper',
    'knowledge.download.subtitle': 'Please enter your contact details to download the white paper.',
    'knowledge.download.firstName': 'First Name',
    'knowledge.download.lastName': 'Last Name',
    'knowledge.download.email': 'Email Address',
    'knowledge.download.company': 'Company (optional)',
    'knowledge.download.cancel': 'Cancel',
    'knowledge.download.submit': 'Download',
    'knowledge.download.submitting': 'Processing...',
    'knowledge.download.success': 'Thank you! The white paper is being downloaded.',
    'knowledge.download.successTitle': 'Download Ready!',
    'knowledge.download.successMessage': 'Thank you for your interest. Your white paper is ready for download.',
    'knowledge.download.downloadNow': 'Download Now',
    'knowledge.download.downloadStarted': 'Download started!',
    'knowledge.download.downloadError': 'Download failed. Please try again later.',
    'knowledge.download.close': 'Close',
    'knowledge.download.errorMessage': 'An error occurred. Please try again.',
    'knowledge.download.privacy': 'Your data will be treated confidentially',
    'knowledge.download.errors.firstName': 'First name is required',
    'knowledge.download.errors.lastName': 'Last name is required',
    'knowledge.download.errors.email': 'Email address is required',
    'knowledge.download.errors.emailInvalid': 'Please enter a valid email address',
    'knowledge.download.errors.company': 'Please enter your company',
    'knowledge.download.errors.phone': 'Please enter your phone number',
    
    'knowledge.cta.title': 'Need Individual AI Consulting?',
    'knowledge.cta.subtitle': 'Let us develop and successfully implement your AI strategy together.',
    'knowledge.cta.button': 'Schedule Consultation',

    // KI-Radar
    'knowledge.radar.badge': 'New: AI Radar',
    'knowledge.radar.title': 'The new AI-RADAR & Benchmark for Companies',
    'knowledge.radar.subtitle': 'Well-founded orientation in AI competition – fact-based, cross-industry, action-oriented.',
    'knowledge.radar.feature1.title': 'International AI Comparative Analysis',
    'knowledge.radar.feature1.description': 'Biannual strategic overview of AI progress in 15 leading countries with focus on DACH region.',
    'knowledge.radar.feature2.title': 'AI Benchmark for Your Company',
    'knowledge.radar.feature2.description': 'In-depth comparative analysis: How well positioned are you compared to competitors?',
    'knowledge.radar.feature3.title': 'Action Recommendations for Decision Makers',
    'knowledge.radar.feature3.description': 'Concrete recommendations for immediate implementation with highest impact.',
    'knowledge.radar.targetAudience.title': 'Who is the AI-RADAR for?',
    'knowledge.radar.targetAudience.ceo': 'CEOs, CIOs and board members with digital responsibility',
    'knowledge.radar.targetAudience.strategists': 'Digitalization and AI strategists',
    'knowledge.radar.targetAudience.managers': 'Transformation managers (250+ employees)',
    'knowledge.radar.newsletter.title': 'Subscribe to AI-RADAR',
    'knowledge.radar.newsletter.subtitle': 'Receive the AI-RADAR biannually directly in your inbox.',
    'knowledge.radar.newsletter.firstNamePlaceholder': 'First Name',
    'knowledge.radar.newsletter.lastNamePlaceholder': 'Last Name',
    'knowledge.radar.newsletter.emailPlaceholder': 'Your email address',
    'knowledge.radar.newsletter.subscribe': 'Subscribe to AI-RADAR',
    'knowledge.radar.newsletter.submitting': 'Subscribing...',
    'knowledge.radar.newsletter.successTitle': 'Successfully subscribed!',
    'knowledge.radar.newsletter.successMessage': 'You will receive the next AI-RADAR directly in your inbox.',
    'knowledge.radar.newsletter.errorMessage': 'Please enter a valid email address.',
    'knowledge.radar.newsletter.errors.firstName': 'First name is required',
    'knowledge.radar.newsletter.errors.lastName': 'Last name is required',
    'knowledge.radar.newsletter.errors.email': 'Email address is required',
    'knowledge.radar.newsletter.errors.emailInvalid': 'Please enter a valid email address',
    'knowledge.radar.newsletter.privacy': 'Your data will be treated confidentially. Unsubscribe anytime.',
    'knowledge.radar.newsletter.frequency': 'per year',
    'knowledge.radar.newsletter.countries': 'Countries',

    // AI Regulations Section
    'knowledge.regulations.title': 'AI Regulations Overview',
    'knowledge.regulations.subtitle': 'In the rapidly evolving world of Artificial Intelligence (AI), it is crucial to understand the current legal frameworks. Here you will find important information about the key regulations that affect the development and deployment of AI.',
    'knowledge.regulations.disclaimer': 'Note: The information provided here is for general informational purposes only and does not constitute legal advice. No liability is accepted for the accuracy, completeness and timeliness of the content.',
    
    // EU AI Act
    'knowledge.regulations.euaiact.title': 'EU Artificial Intelligence Act',
    'knowledge.regulations.euaiact.what.title': 'What is the EU AI Act?',
    'knowledge.regulations.euaiact.what.description': 'The EU AI Act is a legislative proposal by the European Union aimed at making the use of AI systems safe, transparent, and ethically sound. It classifies AI applications into different risk categories and sets specific requirements for each category. The goal is to strengthen trust in AI technologies while promoting innovation.',
    'knowledge.regulations.euaiact.benefit1': 'Promotes the safety and reliability of AI systems',
    'knowledge.regulations.euaiact.benefit2': 'Protects citizens\' fundamental rights',
    'knowledge.regulations.euaiact.benefit3': 'Creates a unified legal framework in the EU',
    'knowledge.regulations.euaiact.benefit4': 'Supports innovation and competitiveness in the EU',
    'knowledge.regulations.euaiact.benefit5': 'Provides clear rules for companies developing and deploying AI',
    'knowledge.regulations.euaiact.link': 'EU AI Act',
    'knowledge.regulations.euaiact.status': 'Status: In force since August 2024',
    
    // GDPR
    'knowledge.regulations.gdpr.title': 'The General Data Protection Regulation (GDPR)',
    'knowledge.regulations.gdpr.what.title': 'What is GDPR?',
    'knowledge.regulations.gdpr.why.title': 'Why is GDPR important for AI?',
    'knowledge.regulations.gdpr.description': 'The EU General Data Protection Regulation (GDPR) regulates the protection of personal data and strengthens the rights of data subjects. It sets strict requirements for the processing of personal data to protect citizens\' privacy. GDPR applies to all companies that process personal data of EU citizens, regardless of the company\'s location.',
    'knowledge.regulations.gdpr.benefit1': 'Ensures transparency and fairness in data processing',
    'knowledge.regulations.gdpr.benefit2': 'Protects users\' privacy and personal data',
    'knowledge.regulations.gdpr.benefit3': 'Strengthens trust in AI systems',
    'knowledge.regulations.gdpr.benefit4': 'Improves data control for individuals',
    'knowledge.regulations.gdpr.benefit5': 'Promotes responsible data practices in companies',
    'knowledge.regulations.gdpr.link': 'To GDPR',
    'knowledge.regulations.gdpr.status': 'Status: In force since May 2018',
    
    // NIS2
    'knowledge.regulations.nis2.title': 'The EU Network and Information Security Directive (NIS2)',
    'knowledge.regulations.nis2.what.title': 'What is NIS2?',
    'knowledge.regulations.nis2.why.title': 'Why is NIS2 important for AI?',
    'knowledge.regulations.nis2.description': 'The NIS2 Directive is the revised version of the original NIS Directive and aims to strengthen cybersecurity in the EU. It expands the scope and tightens requirements for risk management and reporting obligations. NIS2 is designed to ensure that all relevant actors in the EU implement and maintain robust security measures.',
    'knowledge.regulations.nis2.benefit1': 'Increases cybersecurity for AI systems operating or supporting critical infrastructures',
    'knowledge.regulations.nis2.benefit2': 'Introduces stricter reporting obligations for security incidents',
    'knowledge.regulations.nis2.benefit3': 'Improves risk management strategies',
    'knowledge.regulations.nis2.benefit4': 'Expands scope to more sectors and companies',
    'knowledge.regulations.nis2.benefit5': 'Strengthens cooperation and information sharing between member states',
    'knowledge.regulations.nis2.link': 'To NIS2',
    'knowledge.regulations.nis2.status': 'Status: In force since January 2023',

    // AI Use Cases / KI-Fallstudien Page
    'usecases.hero.title': 'AI Use Cases from Practice',
    'usecases.hero.subtitle': 'Discover successful AI implementations across various industries and learn how companies profitably deploy Artificial Intelligence.',
    
    // AICaseHub Section
    'usecases.aicasehub.badge': 'Spin-Off Project',
    'usecases.aicasehub.title': 'AICaseHub.io',
    'usecases.aicasehub.subtitle': 'The comprehensive platform for AI use cases from all industries',
    'usecases.aicasehub.description': 'AICaseHub.io is our spin-off project – a unique collection of AI use cases not only from BROVKO, but also from other AI professionals: from large consulting firms and software manufacturers to local agencies and providers.',
    'usecases.aicasehub.benefit1': 'Wide variety of use cases from diverse industries',
    'usecases.aicasehub.benefit2': 'Detailed insights into technology, risks, and budget',
    'usecases.aicasehub.benefit3': 'Project profiles available for download',
    'usecases.aicasehub.benefit4': 'Direct contact to providers possible',
    'usecases.aicasehub.cta': 'Sign up at AICaseHub.io now',
    'usecases.aicasehub.newsletter': 'Subscribe to Newsletter',
    'usecases.aicasehub.newsletter.description': 'Receive updates on new use cases and developments at AICaseHub.io',
    
    // Newsletter Form
    'usecases.newsletter.title': 'Stay Updated',
    'usecases.newsletter.subtitle': 'Subscribe to our newsletter and receive regular updates on AI trends, best practices, and success stories.',
    'usecases.newsletter.firstName': 'First Name',
    'usecases.newsletter.lastName': 'Last Name',
    'usecases.newsletter.email': 'Email Address',
    'usecases.newsletter.subscribe': 'Subscribe to Newsletter',
    'usecases.newsletter.privacy': 'By subscribing, you accept our privacy policy.',
    'usecases.newsletter.success.title': 'Successfully subscribed!',
    'usecases.newsletter.success.message': 'Thank you for subscribing. You will receive a confirmation email shortly.',
    'usecases.newsletter.submitting': 'Submitting...',

    // Spin-Offs Page
    'spinoffs.hero.title': 'Our Spin-Offs',
    'spinoffs.hero.subtitle': 'From idea to market leader: We incubate innovative solutions, develop them into market-ready products, and successfully launch them in the market. Our aspiration: Not just to be at the forefront of AI topics, but also to understand the market and have the implementation power.',
    'spinoffs.intro.title': 'Our Start-Up Incubation',
    'spinoffs.intro.description': 'From our years of experience in AI consulting, we identify promising market opportunities, incubate new ideas, and systematically develop them into scalable products. Through intensive market analyses and our implementation expertise, innovative start-ups emerge that fill specific market gaps.',
    'spinoffs.process.title': 'Our Incubation Process',
    'spinoffs.process.step1': 'Idea Incubation',
    'spinoffs.process.step1.desc': 'Identification and development of innovative AI solution approaches',
    'spinoffs.process.step2': 'Product Development',
    'spinoffs.process.step2.desc': 'Transformation of ideas into market-ready products',
    'spinoffs.process.step3': 'Scaling',
    'spinoffs.process.step3.desc': 'Building scalable business models and infrastructures',
    'spinoffs.process.step4': 'Market Launch',
    'spinoffs.process.step4.desc': 'Successful positioning and growth in the market',
    'spinoffs.current.title': 'Our Current Start-Ups',
    'spinoffs.current.subtitle': 'These innovative companies have emerged from our incubation process and are already conquering their respective markets.',
    
    // AIWizz
    'spinoffs.aiwizz.badge': 'AI Enterprise Solution',
    'spinoffs.aiwizz.title': 'AIWizz',
    'spinoffs.aiwizz.subtitle': 'Private LLM Platform for Enterprises',
    'spinoffs.aiwizz.description': 'AIWizz is more than just a private enterprise GPT. It is an orchestrating, role-based AI platform that enables companies to use the latest LLM models while keeping all data securely within the company.',
    'spinoffs.aiwizz.feature1': '100% GDPR-compliant Private LLM',
    'spinoffs.aiwizz.feature2': 'Multi-agent systems for complex processes',
    'spinoffs.aiwizz.feature3': 'Cross-system integration (CRM, ERP, CMS)',
    'spinoffs.aiwizz.feature4': 'VoiceWizz - Intelligent voice control',
    'spinoffs.aiwizz.feature5': 'Role-based control and workflows',
    'spinoffs.aiwizz.cta': 'Learn more about AIWizz',
    
    // AICaseHub
    'spinoffs.aicasehub.badge': 'Use Case Platform',
    'spinoffs.aicasehub.title': 'AICaseHub.io',
    'spinoffs.aicasehub.subtitle': 'The comprehensive AI Use Case Platform',
    'spinoffs.aicasehub.description': 'A unique collection of AI use cases - not only from BROVKO, but also from other AI professionals: from large consulting firms to local agencies. Companies find concrete inspiration and detailed implementation guidance here.',
    'spinoffs.aicasehub.feature1': 'Wide variety of use cases from diverse industries',
    'spinoffs.aicasehub.feature2': 'Detailed technology and budget insights',
    'spinoffs.aicasehub.feature3': 'Project profiles available for download',
    'spinoffs.aicasehub.feature4': 'Direct contact to providers',
    'spinoffs.aicasehub.feature5': 'Inspiration for own AI projects',
    'spinoffs.aicasehub.cta': 'Visit AICaseHub.io',
    
    // Investor Section
    'spinoffs.investors.title': 'Investor Inquiries',
    'spinoffs.investors.subtitle': 'Are you interested in investment opportunities in our innovative spin-offs? Contact us for detailed information and business opportunities.',
    'spinoffs.investors.firstName': 'First Name',
    'spinoffs.investors.lastName': 'Last Name',
    'spinoffs.investors.email': 'Email Address',
    'spinoffs.investors.company': 'Company',
    'spinoffs.investors.phone': 'Phone Number',
    'spinoffs.investors.submit': 'Send Investor Inquiry',
    'spinoffs.investors.privacy': 'By submitting, you accept our privacy policy.',
    'spinoffs.investors.success.title': 'Inquiry successfully sent!',
    'spinoffs.investors.success.message': 'Thank you for your interest. We will contact you shortly with detailed information.',
    'spinoffs.investors.submitting': 'Submitting...',
    
    // Industries
    'usecases.industries.title': 'Industries in Focus',
    'usecases.industries.subtitle': 'AI solutions for all industries and business areas',
    'usecases.industries.retail': 'Retail',
    'usecases.industries.manufacturing': 'Manufacturing',
    'usecases.industries.banking': 'Banking',
    'usecases.industries.insurance': 'Insurance',
    'usecases.industries.pe': 'Private Equity',
    'usecases.industries.automotive': 'Automotive',
    'usecases.industries.logistics': 'Logistics',
    'usecases.industries.marketing': 'Marketing',
    'usecases.industries.sales': 'Sales',
    'usecases.industries.it': 'IT',
    'usecases.industries.service': 'Service',
    'usecases.industries.controlling': 'Controlling',
    'usecases.industries.datascience': 'Data Science',
    'usecases.industries.healthcare': 'Healthcare',
    'usecases.industries.education': 'Education',
    
    // BROVKO Case Studies
    'usecases.brovko.title': 'BROVKO Case Studies',
    'usecases.brovko.subtitle': 'Selected success stories from our AI implementations',
    
    // Case Study 1
    'usecases.case1.title': 'AI-powered Sales Assistant for Wholesaler',
    'usecases.case1.industry': 'Wholesale & Logistics',
    'usecases.case1.description': 'More efficient customer service and faster decisions through Artificial Intelligence',
    'usecases.case1.result1': '35% faster quote processes',
    'usecases.case1.result2': '20% revenue increase',
    'usecases.case1.result3': '50% less manual research time',
    'usecases.case1.technology': 'Private LLM, GDPR-compliant architecture',
    
    // Case Study 2
    'usecases.case2.title': 'AI-powered 24/7 Support in Machine Manufacturing',
    'usecases.case2.industry': 'Machine Manufacturing & Plant Engineering',
    'usecases.case2.description': 'International customer and dealer support through AI-controlled assistants in over 12 languages',
    'usecases.case2.result1': '52% faster support responses',
    'usecases.case2.result2': '24% higher first-call resolution',
    'usecases.case2.result3': '32% less machine downtime',
    'usecases.case2.technology': 'Private LLM, Multilingual AI, CRM/ERP Integration',
    
    // CTA Section
    'usecases.cta.title': 'Ready for your own AI success story?',
    'usecases.cta.subtitle': 'Let us develop and implement your AI project together.',
    'usecases.cta.button': 'Schedule free consultation',

    // KI-Beratung Page (English)
    'kiBeratung.hero.badge': 'AI Consulting',
    'kiBeratung.hero.title': 'Tailored AI Strategies for Sustainable Business Success',
    'kiBeratung.hero.description': 'The successful use of Artificial Intelligence (AI) requires a clear strategy, targeted implementation and sustainable integration into existing business processes. Our AI consulting helps companies use AI strategically, secure competitive advantages and achieve long-term success.',
    'kiBeratung.hero.subtitle': 'We accompany you from strategic planning to practical implementation – with solid expertise, proven methods and AI-supported digital checklists for maximum efficiency.',
    'kiBeratung.hero.cta': 'Book consultation',
    
    'kiBeratung.whyBrovko.title': 'Why AI Consulting from Brovko?',
    'kiBeratung.services.title': 'Our Consulting Services',
    'kiBeratung.additional.title': 'Additional Consulting Areas',
    'kiBeratung.industry.title': 'Industry-Oriented AI Consulting',
    'kiBeratung.industry.subtitle': 'Tailored solutions for your business',
    'kiBeratung.methodology.title': 'Our Methodology',
    'kiBeratung.methodology.description': 'We offer holistic AI consulting that is not limited to strategic concepts, but delivers practical, implementable solutions:',
    'kiBeratung.cta.title': 'Start your individual AI consulting now!',
    'kiBeratung.cta.description': 'Would you like to know how to optimally use AI in your company? Let us develop a customized AI strategy together and successfully implement your AI transformation.',

    // KI-Transformation Page (English)
    'kiTransformation.hero.badge': 'AI Transformation',
    'kiTransformation.hero.title': 'Holistic AI Transformation for Sustainable Business Success',
    'kiTransformation.hero.description': 'AI transformation means more than just technology implementation. We accompany you on the path to becoming an AI-driven company – from strategic vision to sustainable integration in your organization.',
    'kiTransformation.hero.subtitle': 'Structured 4-phase approach with proven methods, measurable success and sustainable enablement of your teams.',
    'kiTransformation.hero.cta': 'Plan Transformation',
    
    'kiTransformation.whyBrovko.title': 'Why AI Transformation with Brovko?',
    'kiTransformation.phases.title': 'Our 4-Phase Transformation Model',
    'kiTransformation.services.title': 'Our Transformation Services',
    'kiTransformation.industry.title': 'Industry-Specific Transformations',
    'kiTransformation.industry.subtitle': 'Successful AI transformations across various industries',
    'kiTransformation.success.title': 'Success Factors for Sustainable AI Transformation',
    'kiTransformation.success.subtitle': 'These factors determine the success of your AI transformation:',
    'kiTransformation.cta.title': 'Ready for your AI Transformation?',
    'kiTransformation.cta.description': 'Let us lay the foundation for your successful AI future together. Start with a free strategy consultation.',

    // KI-Coaching Page (English)
    'kiCoaching.hero.badge': 'AI Coaching & AI Advisory',
    'kiCoaching.hero.title': 'Sparring Partner for Your AI Transformation – At Eye Level',
    'kiCoaching.hero.description': 'AI is not a short-term trend, but a strategic game-changer. BROVKO accompanies you with an exclusive C-level coaching offer – personal, confidential and impact-oriented.',
    'kiCoaching.hero.subtitle': 'Our goal: Help you do the right things – and then implement them as effectively as possible.',
    'kiCoaching.hero.cta': 'Book Initial Meeting',
    
    'kiCoaching.challenges.title': 'Many Companies Face the Challenge',
    'kiCoaching.challenges.subtitle': 'AI leadership requires strategic thinking and practical implementation competence',
    'kiCoaching.whyBrovko.title': 'Why AI Coaching with Brovko?',
    'kiCoaching.services.title': 'Our Coaching Services',
    'kiCoaching.benefits.title': 'Your Added Value',
    'kiCoaching.topics.title': 'Coaching Focus Areas',
    'kiCoaching.audience.title': 'Who We Work For',
    'kiCoaching.audience.subtitle': 'Exclusive C-level support for strategic AI decisions',
    'kiCoaching.cta.title': 'Contact Us!',
    'kiCoaching.cta.description': 'Book a free and non-binding introductory meeting and learn how we can support you in your AI transformation.',

    // KI-Workshops Page (English)
    'kiWorkshops.hero.badge': 'AI Workshops',
    'kiWorkshops.hero.title': 'Individual AI Workshops for Your Company – Practical and Results-Oriented',
    'kiWorkshops.hero.description': 'The successful introduction of Artificial Intelligence (AI) begins with the right knowledge. Our AI workshops offer you and your team a fast, practical and strategic introduction to central AI topics.',
    'kiWorkshops.hero.subtitle': 'Individually tailored to your needs, each workshop combines expertise with directly implementable results.',
    'kiWorkshops.hero.cta': 'Book Workshop',
    
    'kiWorkshops.whyBrovko.title': 'Why AI Workshops from Brovko?',
    'kiWorkshops.workshops.title': 'Our AI Workshops',
    'kiWorkshops.formats.title': 'Workshop Formats',
    'kiWorkshops.methodology.title': 'Our Methodology',
    'kiWorkshops.methodology.subtitle': 'Our workshops are more than just theory. We rely on interactive formats, real business cases and AI-supported digital tools to achieve sustainable results.',
    'kiWorkshops.audience.title': 'Who Are Our Workshops For?',
    'kiWorkshops.audience.subtitle': 'Customized workshop content for different target groups',
    'kiWorkshops.cta.title': 'Book Your Individual AI Workshop Now!',
    'kiWorkshops.cta.description': 'Each workshop is tailored to your specific requirements. Let us find out together how AI can advance your company.',

    // KI-Anwendungen Page (English)
    'kiAnwendungen.hero.badge': 'AI Applications',
    'kiAnwendungen.hero.title': 'Individual AI Solutions for Your Company – Secure, Efficient and High-Performance',
    'kiAnwendungen.hero.description': 'Artificial Intelligence (AI) opens up completely new possibilities for companies – from automating processes to intelligently optimizing business models. We support you in developing customized AI applications that are precisely tailored to your requirements.',
    'kiAnwendungen.hero.subtitle': 'Whether Private LLMs, AI-supported enterprise platforms or smart extensions for existing systems – our solutions increase your efficiency, improve your competitiveness and meet the highest data protection and compliance standards.',
    'kiAnwendungen.hero.cta': 'Start AI Project - Book Consultation',
    
    'kiAnwendungen.whyBrovko.title': 'Why AI Applications with Brovko?',
    'kiAnwendungen.applications.title': 'Our AI Solutions',
    'kiAnwendungen.useCases.title': 'AI Applications in Practice',
    'kiAnwendungen.methodology.title': 'Our Methodology – Develop & Integrate AI Precisely',
    'kiAnwendungen.methodology.subtitle': 'Our AI experts accompany you from the first idea to the finished implementation',
    'kiAnwendungen.security.title': 'Highest Security Standards',
    'kiAnwendungen.security.subtitle': 'Company data remains safe and protected',
    'kiAnwendungen.cta.title': 'Start Your Individual AI Project Now!',
    'kiAnwendungen.cta.description': 'Would you like to know how to use AI specifically in your company? Let us develop a customized AI solution together – secure, powerful and future-proof.',

    // KI-Plattform (AIWizz) Page (English)
    'kiPlattform.hero.badge': 'AI Platform AIWizz',
    'kiPlattform.hero.title': 'Your Private GPT for Maximum Productivity – Secure and Powerful',
    'kiPlattform.hero.description': 'AIWizz enables companies to operate their own private GPT environment – without data leaving the company. Use the latest LLM technologies without concerns for your business.',
    'kiPlattform.hero.subtitle': 'From GPT assistants for every employee to multi-agent systems – AIWizz makes your teams more productive, effective and smarter.',
    'kiPlattform.hero.cta': 'Book AIWizz Demo',
    'kiPlattform.hero.ctaSecondary': 'Visit AIWizz Site',
    
    'kiPlattform.whyAIWizz.title': 'Why AIWizz for Your Company?',
    'kiPlattform.features.title': 'Platform Features',
    'kiPlattform.useCases.title': 'AI Applications by Department',
    'kiPlattform.models.title': 'Supported LLM Models',
    'kiPlattform.models.subtitle': 'Access to the best and latest AI technologies',
    'kiPlattform.security.title': 'Highest Security Standards',
    'kiPlattform.security.subtitle': 'Your data stays 100% within your company',
    'kiPlattform.cta.title': 'Get Started with AIWizz!',
    'kiPlattform.cta.description': 'Experience the power of a private AI platform for your company. Book a demo and see how AIWizz revolutionizes your productivity.',

    // AI-Optimized FAQ Section
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about AI Transformation and custom made AI applications',
    'faq.cta.title': 'Still have questions?',
    'faq.cta.subtitle': 'Our AI transformation experts are here to help you',  
    'faq.cta.button': 'Contact Us',

    'faq.valueEngine.question': 'What is the AI Value Engine?',
    'faq.valueEngine.answer': 'The AI Value Engine is BROVKO\'s methodology to make AI transformation measurable. It includes portfolio management for all AI initiatives, a value dashboard for CEO-ready reporting with EBIT impact, and an operating rhythm for continuous control. This transforms AI from a hopeful prospect into a controllable value driver.',

    'faq.valueCheck.question': 'What is the AI Value Check for?',
    'faq.valueCheck.answer': 'The AI Value Check is a free, 3-minute self-assessment that determines your AI maturity level. In 7 questions, we analyze your current situation regarding pilot projects, measurability, governance, and budget. At the end, you receive a personalized analysis with concrete recommendations and can directly request an executive briefing.',

    'faq.kiTransformation.question': 'What is AI Transformation?',
    'faq.kiTransformation.answer': 'AI Transformation is the comprehensive process of integrating Artificial Intelligence into companies. BROVKO guides you from strategy development to productive application and offers custom made AI applications for sustainable business success.',

    'faq.customAI.question': 'What are custom made AI applications?',
    'faq.customAI.answer': 'Custom made AI applications are tailor-made AI solutions specifically developed for your company\'s needs. This includes Private GPT models, Enterprise AI platforms, and GDPR-compliant AI systems.',

    'faq.aiVsDigital.question': 'How does AI Transformation differ from digital transformation?',
    'faq.aiVsDigital.answer': 'AI Transformation goes beyond classic digitalization and focuses on intelligent automation, data analysis, and machine learning. BROVKO develops sustainable AI strategies that deliver measurable business results.',

    'faq.industries.question': 'Which industries benefit from AI Transformation?',
    'faq.industries.answer': 'All industries benefit from AI Transformation: Production & Manufacturing (25% less downtime), Financial services (40% faster decisions), Retail (20% higher conversion), Healthcare (50% faster diagnoses).',

    'faq.timeline.question': 'How long does an AI Transformation take?',
    'faq.timeline.answer': 'An AI Transformation at BROVKO takes place in 4 phases: Strategic alignment, organizational development, technical implementation, and scaling. Depending on company size, the process takes 3-12 months for sustainable results.',

    'faq.roi.question': 'What ROI can I expect from AI Transformation?',
    'faq.roi.answer': 'Our clients typically see 20-40% efficiency improvements within the first year. ROI varies by industry but includes cost savings through automation, increased revenue through better decision-making, and competitive advantages through custom made AI applications.',

    // Contact Modal Translations
    'contact.modal.title': 'Get in Touch',
    'contact.modal.subtitle': 'Book an AI Consultation',
    'contact.modal.placeholder': 'Your message...',
    'contact.modal.firstName': 'First Name',
    'contact.modal.lastName': 'Last Name',
    'contact.modal.email': 'Email',
    'contact.modal.phone': 'Phone Number',
    'contact.modal.message': 'Your Message',
    'contact.modal.cancel': 'Cancel',
    'contact.modal.submit': 'Send Message',
    'contact.modal.submitting': 'Sending...',
    'contact.modal.success.title': 'Message sent successfully!',
    'contact.modal.success.text': 'Thank you for your interest. We will get back to you quickly.',
    'contact.modal.error.title': 'Error sending message',
    'contact.modal.error.text': 'Sorry, an error occurred while sending the message. Please try again.',
    'contact.modal.error.firstName': 'First name is required',
    'contact.modal.error.lastName': 'Last name is required',
    'contact.modal.error.email': 'Valid email address is required',

    // KI-MVP Development Page
    'mvp.seo.title': 'AI-MVP Development in 3-4 Weeks | 19,000 EUR Fixed Price | BROVKO',
    'mvp.seo.description': 'Your working AI-MVP in just 3-4 weeks at a fixed price of 19,000 EUR. Fast market entry with tangible results and minimized risk.',
    'mvp.seo.keywords': 'AI-MVP, MVP development, AI prototype, Proof of Concept, AI development, fixed price, 4 weeks',

    'mvp.hero.badge': 'Rapid AI Implementation',
    'mvp.hero.title': 'AI-MVP in 3–4 Weeks',
    'mvp.hero.subtitle': 'A working AI-MVP at a fixed price – tangible results instead of lengthy projects. Test your AI idea quickly and with minimal risk.',
    'mvp.hero.timeline': '3–4 weeks development time',
    'mvp.hero.cta': 'Start MVP Project Now',

    // AI Lab Page
    'kiLab.seo.title': 'External AI Lab | Ready to Use Without Your Own Team | BROVKO',
    'kiLab.seo.description': 'Your external AI lab - from first AI idea to your own AI competency in 4 well-thought phases. Immediately available, predictable costs, real results.',
    'kiLab.seo.keywords': 'External AI Lab, AI Team, AI Outsourcing, AI Partnership, AI Sovereignty, AI Competency Building',

    'kiLab.hero.title': 'Your External AI Lab – Ready to Use, Without Your Own Team',
    'kiLab.hero.subtitle': 'From first AI idea to your own AI competency – in 4 well-thought phases',
    'kiLab.hero.problem': 'Why wait 6-12 months for your AI team to be ready? Start today with an experienced external AI lab.',
    'kiLab.hero.cta': 'Schedule 30-Minute Conversation',

    'kiLab.problem.title': 'The Problem with Internal AI Labs',
    'kiLab.problem.point1': 'Recruitment takes months, setup costs a fortune',
    'kiLab.problem.point2': 'First projects are delayed accordingly',
    'kiLab.problem.point3': 'Unpredictable salaries for AI specialists',

    'kiLab.solution.title': 'Our Solution: Brovko as Your External AI Lab',
    'kiLab.solution.description': 'The concept is simple: You get immediate access to experienced AI developers – without recruitment. We implement your projects – fast, at fixed prices, with real results.',
    'kiLab.solution.highlight': 'Strategic partnership with clear goal: Your AI sovereignty.',

    'kiLab.phases.title': '4-Phase Model: Your Path to AI Sovereignty',
    'kiLab.phases.subtitle': 'From first idea to complete independence',

    'kiLab.phases.phase1.title': 'Phase 1: Quick Start',
    'kiLab.phases.phase1.description': 'You have a concrete problem or idea? We deliver you a working MVP in 3–4 weeks.',
    'kiLab.phases.phase1.duration': '3–4 weeks',

    'kiLab.phases.phase2.title': 'Phase 2: Continuous Development',
    'kiLab.phases.phase2.description': 'We work as your outsourced AI lab: regular sprints, fixed capacities, predictable costs.',
    'kiLab.phases.phase2.duration': 'Ongoing',

    'kiLab.phases.phase3.title': 'Phase 3: Knowledge Transfer',
    'kiLab.phases.phase3.description': 'We train your employees, document our processes and help with step-by-step competency building.',
    'kiLab.phases.phase3.optional': 'Optional',

    'kiLab.phases.phase4.title': 'Phase 4: Your Own AI Lab',
    'kiLab.phases.phase4.description': 'You take over completely. We have brought you to the point where you can work independently.',
    'kiLab.phases.phase4.result': 'AI sovereignty achieved',

    'kiLab.benefits.title': 'Why This Works',
    'kiLab.benefits.benefit1.title': 'Save 6–12 Months Lead Time',
    'kiLab.benefits.benefit1.description': 'Immediate start without lengthy team building',
    'kiLab.benefits.benefit2.title': 'Fixed Costs Instead of Unpredictable Salaries',
    'kiLab.benefits.benefit2.description': 'Predictable budgets for AI specialists',
    'kiLab.benefits.benefit3.title': 'Immediate Results Instead of Ramp-Up Phases',
    'kiLab.benefits.benefit3.description': 'Functional solutions from day 1',
    'kiLab.benefits.benefit4.title': 'Flexible Exit – No Dependency',
    'kiLab.benefits.benefit4.description': 'You do not remain dependent on us',

    'kiLab.differentiator.title': 'This is Not Classic Outsourcing',
    'kiLab.differentiator.description': 'It is a strategic partnership with clear goal: Your AI sovereignty.',
    'kiLab.differentiator.not.title': 'No Outsourcing',
    'kiLab.differentiator.not.description': 'Long-term dependency and standard solutions',
    'kiLab.differentiator.is.title': 'Strategic Partnership',
    'kiLab.differentiator.is.description': 'Knowledge transfer and building your own competencies',

    'kiLab.audience.title': 'Who Is This For?',
    'kiLab.audience.point1': 'You want to implement multiple AI projects, not just one MVP',
    'kiLab.audience.point2': 'You plan your own AI team medium-term but want to start now',
    'kiLab.audience.point3': 'You need reliable capacities without long recruiting',
    'kiLab.audience.point4': 'You want to learn while building',

    'kiLab.cta.title': 'Interested? Let Us Talk.',
    'kiLab.cta.description': 'A 30-minute conversation is enough to find out if this model fits you.',
    'kiLab.cta.email.title': 'Send Message',
    'kiLab.cta.email.description': 'Describe your concern',
    'kiLab.cta.email.button': 'Send Message',
    'kiLab.cta.mvpLink.text': 'More info about AI application MVPs:',
    'kiLab.cta.mvpLink.linkText': 'AI MVP Development',
    'kiLab.cta.mainButton': 'Schedule AI Lab Conversation Now',

    // Private Equity Whitepaper Page
    'pe.seo.title': 'AI White Paper Series for Private Equity | Value Drivers & Creation | BROVKO',
    'pe.seo.description': 'Exclusive white paper series for PE firms: AI as value driver from deal sourcing to exit. Cost reduction, revenue growth, cash optimization & buy & build.',
    'pe.seo.keywords': 'Private Equity AI, PE White Paper, AI Value Creation, PE Portfolio Optimization, AI Private Equity, Buy and Build AI',

    'pe.hero.badge': 'Exclusive for Private Equity',
    'pe.hero.title': 'AI as Value Driver for Private Equity',
    'pe.hero.subtitle': 'Exclusive white paper series: The 4 decisive AI levers for portfolio value creation and exit readiness',
    'pe.hero.benefit1': 'Fast ROI realization',
    'pe.hero.benefit2': 'Exit-relevant documentation',
    'pe.hero.benefit3': 'Portfolio-wide scaling',

    'pe.problem.title': 'Private Equity Under Pressure',
    'pe.problem.description': 'Traditional value creation levers are reaching their limits. AI opens up completely new possibilities for the entire PE value chain.',

    'pe.problem.challenges.title': 'Current Challenges',
    'pe.problem.challenges.point1': 'Rising purchase prices with increasing competition',
    'pe.problem.challenges.point2': 'Traditional levers reaching their limits',
    'pe.problem.challenges.point3': 'Pressure for fast, significant value creation',

    'pe.problem.solution.title': 'AI as Game Changer',
    'pe.problem.solution.description': 'Artificial Intelligence revolutionizes the PE value chain:',
    'pe.problem.solution.point1': 'Deal sourcing & due diligence acceleration',
    'pe.problem.solution.point2': 'Portfolio management with precise forecasts',
    'pe.problem.solution.point3': 'Exit readiness through documented AI capabilities',

    'pe.selection.title': 'Select Your White Papers',
    'pe.selection.subtitle': 'The 4 central AI levers for Private Equity - newly interpreted through Artificial Intelligence',

    'pe.whitepaper.cost.title': 'Cost Reduction with AI',
    'pe.whitepaper.cost.description': 'Efficiency improvements, process automation and predictive maintenance for portfolio companies',

    'pe.whitepaper.revenue.title': 'Revenue Growth with AI',
    'pe.whitepaper.revenue.description': 'AI-powered pricing, personalization and cross-/upselling strategies',

    'pe.whitepaper.cash.title': 'Cash Optimization with AI',
    'pe.whitepaper.cash.description': 'Precise forecasts, working capital management and intelligent liquidity control',

    'pe.whitepaper.buybuild.title': 'Buy & Build with AI',
    'pe.whitepaper.buybuild.description': 'Faster target screening, more precise synergy calculation and accelerated integration',

    'pe.form.title': 'Request White Papers Now',
    'pe.form.subtitle': 'Enter your contact details and receive the selected white papers immediately via email.',
    'pe.form.firstName': 'First Name',
    'pe.form.lastName': 'Last Name',
    'pe.form.email': 'Email Address',
    'pe.form.phone': 'Phone Number (optional)',
    'pe.form.selectedWhitepapers': 'Selected White Papers',
    'pe.form.submit': 'Request White Papers',
    'pe.form.submitting': 'Sending...',
    'pe.form.privacy': 'Your data will be treated confidentially and used exclusively for sending the white papers.',

    'pe.form.success.title': 'White Papers Being Sent!',
    'pe.form.success.message': 'Thank you for your interest. The selected white papers have been sent to your email address.',

    'pe.form.errors.firstName': 'First name is required',
    'pe.form.errors.lastName': 'Last name is required',
    'pe.form.errors.email': 'Email address is required',
    'pe.form.errors.emailInvalid': 'Please enter a valid email address',
    'pe.form.errors.whitepapers': 'Please select at least one white paper',
    'pe.form.errors.submit': 'Error sending. Please try again.',

    'pe.social.firms': 'PE firms trust us',
    'pe.social.aum': 'AUM of our clients',
    'pe.social.portfolio': 'Portfolio companies',
    'pe.social.testimonial': 'The AI implementation has measurably improved our portfolio performance. BROVKO helped us identify new value creation potentials.',
    'pe.social.testimonialAuthor': '— Managing Partner, German PE Firm',

    // KI Organisation Whitepaper Page
    'org.seo.title': 'AI & Human in Organization White Papers | Change Management | BROVKO',
    'org.seo.description': 'White paper series: AI & Organization, Human in the Loop, AI & Workplace. How companies successfully manage AI transformation without losing the human element.',
    'org.seo.keywords': 'AI Organization, Human in the Loop, AI Workplace, Change Management AI, AI Transformation Company',

    'org.hero.badge': 'For Decision Makers & Leaders',
    'org.hero.title': 'AI & Human in Organization',
    'org.hero.subtitle': 'White paper series: How to align your organization for the AI era – without losing the human element',
    'org.hero.benefit1': 'CEO Action Framework',
    'org.hero.benefit2': 'New Work Division',
    'org.hero.benefit3': 'Future Workplace',

    'org.problem.title': 'AI changes everything – but how to organize?',
    'org.problem.description': 'Artificial Intelligence changes value creation, collaboration and roles faster than any technology before. Many companies face the question: How do I organize this transformation?',

    'org.problem.challenges.title': 'The Challenge',
    'org.problem.challenges.point1': 'AI fundamentally changes roles and collaboration',
    'org.problem.challenges.point2': 'Employees have fears about job loss',
    'org.problem.challenges.point3': 'Leadership often doesn\'t know where to start',

    'org.problem.solution.title': 'The Solution Path',
    'org.problem.solution.description': 'Our white paper series shows the way:',
    'org.problem.solution.point1': 'CEO action framework for AI transformation',
    'org.problem.solution.point2': 'New work division between human and AI',
    'org.problem.solution.point3': 'Workplace evolution instead of revolution',

    'org.selection.title': 'Select Your White Papers',
    'org.selection.subtitle': 'Three perspectives on AI & Human in organizations – from leadership level to workplace',

    'org.whitepaper.organisation.title': 'AI & Organization – Why CEOs Must Act',
    'org.whitepaper.organisation.description': 'Strategic action framework for leaders: How to successfully steer AI transformation in your organization',

    'org.whitepaper.humanloop.title': 'Human in the Loop – New Work Division Between Human and AI',
    'org.whitepaper.humanloop.description': 'Practical approaches for optimal collaboration: Where humans remain indispensable, where AI takes over',

    'org.whitepaper.workplace.title': 'AI & the Workplace – How Every Job Changes',
    'org.whitepaper.workplace.description': 'Concrete impacts on workplaces: Which jobs change how and how employees can prepare',

    'org.form.title': 'Request White Papers Now',
    'org.form.subtitle': 'Enter your contact details and receive the selected white papers immediately via email.',
    'org.form.firstName': 'First Name',
    'org.form.lastName': 'Last Name',
    'org.form.email': 'Email Address',
    'org.form.phone': 'Phone Number (optional)',
    'org.form.selectedWhitepapers': 'Selected White Papers',
    'org.form.submit': 'Request White Papers',
    'org.form.submitting': 'Sending...',
    'org.form.privacy': 'Your data will be treated confidentially and used exclusively for sending the white papers.',

    'org.form.success.title': 'White Papers Being Sent!',
    'org.form.success.message': 'Thank you for your interest. The selected white papers have been sent to your email address.',

    'org.form.errors.firstName': 'First name is required',
    'org.form.errors.lastName': 'Last name is required',
    'org.form.errors.email': 'Email address is required',
    'org.form.errors.emailInvalid': 'Please enter a valid email address',
    'org.form.errors.whitepapers': 'Please select at least one white paper',
    'org.form.errors.submit': 'Error sending. Please try again.',

    'org.social.companies': 'Companies trust us',
    'org.social.success': 'Success rate',
    'org.social.leader': 'AI pioneer',
    'org.social.testimonial': 'The structured approach helped us successfully implement AI without losing our employees.',
    'org.social.testimonialAuthor': '— HR Director, DAX Company',

    'mvp.whatYouGet.title': 'What You Get in the End',
    'mvp.benefits.testing.title': 'Immediately Testable Value',
    'mvp.benefits.testing.desc': 'A working software product, application, or app that allows you to test concrete value immediately.',
    'mvp.benefits.acceptance.title': 'Test User Acceptance',
    'mvp.benefits.acceptance.desc': 'Test acceptance internally and externally – before making larger investments.',
    'mvp.benefits.speed.title': 'Save Time & Budget',
    'mvp.benefits.speed.desc': 'Your only task: a short project briefing with us. We take care of the rest.',

    'mvp.additional.title': 'Additional Benefits',
    'mvp.advantages.market.title': 'Fast Market Entry',
    'mvp.advantages.market.desc': 'To market in weeks instead of months – gain a time advantage.',
    'mvp.advantages.flexibility.title': 'Maximum Flexibility',
    'mvp.advantages.flexibility.desc': 'MVPs can be easily extended and adapted – perfect for iterative development.',
    'mvp.advantages.risk.title': 'Risk Minimization',
    'mvp.advantages.risk.desc': 'Invest only in what really works and provides value.',
    'mvp.advantages.learning.title': 'Build Competence',
    'mvp.advantages.learning.desc': 'Learn which AI approaches provide the greatest benefit for your company.',

    'mvp.bestPart.title': 'The Best Part',
    'mvp.bestPart.description': 'Experience with us how AI can be used productively and meaningfully. You gain not only a finished result, but also competence in dealing with AI.',
    'mvp.bestPart.benefit1': 'Finished Result',
    'mvp.bestPart.benefit2': 'AI Competence',
    'mvp.bestPart.cta': 'Implement Your Idea as MVP',

    'mvp.contact.defaultMessage': 'I am interested in AI-MVP development and want to implement my idea in 3-4 weeks.',

    // Partner Program - EN
    'partner.seo.title': 'Partner Program for IT Service Providers | BROVKO',
    'partner.seo.description': 'You remain the owner of your customer relationships – we complement your services with reliable AI expertise.',
    'partner.seo.keywords': 'IT service providers, AI partner, white-label, subcontractor, AI expertise, system integrator',

    'partner.hero.title': 'Partner Program for IT Service Providers',
    'partner.hero.subtitle': 'You remain the owner of your customer relationships – we complement your services with reliable AI expertise.',

    'partner.intro.title': 'What is the BROVKO Partner Program?',
    'partner.intro.p1': 'The BROVKO Partner Program is aimed at IT service providers who face concrete AI requests from their customers but do not want to fully cover these topics themselves.',
    'partner.intro.p2': 'The program enables joint implementation of AI projects without jeopardizing existing customer relationships or having to build your own AI teams.',
    'partner.intro.p3': 'The IT service provider remains the main point of contact and owner of the customer relationship. BROVKO supplements the necessary AI competence within the agreed framework – from strategic classification to operational implementation.',

    'partner.situation.title': 'Why This Program Exists',
    'partner.situation.intro': 'We see the same situation among many IT service providers:',
    'partner.situation.point1': 'Customers specifically ask for AI (chatbots, automation, internal GPTs, knowledge systems, decision support)',
    'partner.situation.point2': 'The interest is real, no longer experimental',
    'partner.situation.challenge': 'At the same time, it is clear:',
    'partner.situation.challenge1': 'AI is not a classic IT topic',
    'partner.situation.challenge2': 'False promises or half-baked solutions damage customer relationships',
    'partner.situation.challenge3': 'Building your own AI teams is expensive, risky, and slow',
    'partner.situation.dilemma': 'Many IT service providers therefore face a dilemma:',
    'partner.situation.dilemmaText': '"We don\'t want to lose our customers – but we don\'t want to burn ourselves with AI either."',
    'partner.situation.solution': 'This is exactly where the BROVKO partner program comes in.',

    'partner.coreIdea.title': 'Core Idea in One Sentence',
    'partner.coreIdea.text': 'You remain the owner of your customer relationships – we complement your services with reliable AI expertise.',
    'partner.coreIdea.subtitle': 'No more. But no less either.',

    'partner.whoBrovko.title': 'Who BROVKO Is – and Who We Deliberately Are Not',
    'partner.whoBrovko.what.title': 'What We Are',
    'partner.whoBrovko.what.point1': 'Specialist for AI strategy, AI architecture, and operational AI implementation',
    'partner.whoBrovko.what.point2': 'Experience from strategic assessment to productive operation',
    'partner.whoBrovko.what.point3': 'Own AI products: AICaseHub.io (use cases, structure, orientation) and AIWizz.ai (operational AI solutions)',
    'partner.whoBrovko.whatNot.title': 'What We Are Not',
    'partner.whoBrovko.whatNot.point1': 'Not a classic IT service provider',
    'partner.whoBrovko.whatNot.point2': 'Not a system house',
    'partner.whoBrovko.whatNot.point3': 'Not a competitor for infrastructure, software, or operations projects',
    'partner.whoBrovko.whatNot.point4': 'Not an acquisition partner who "takes over" customers',
    'partner.whoBrovko.note': 'This is important because this is exactly where many partnerships fail.',

    'partner.centralQuestion.title': 'The Central Question',
    'partner.centralQuestion.question': '"Are you poaching our customers?"',
    'partner.centralQuestion.answer': 'No. Period.',
    'partner.centralQuestion.principle': 'The partner program is based on a simple principle:',
    'partner.centralQuestion.rule1': 'No direct approach to customers',
    'partner.centralQuestion.rule2': 'No acquisition from existing customers',
    'partner.centralQuestion.rule3': 'No bypassing the partner',
    'partner.centralQuestion.rule4': 'No competition for follow-up projects',
    'partner.centralQuestion.onRequest': 'BROVKO only appears at the request of the partner and only within the agreed framework.',
    'partner.centralQuestion.separation': 'If a customer approaches us without reference to the partner, they remain our customer. If a customer comes through the partner, they remain their customer.',
    'partner.centralQuestion.nonnegotiable': 'This separation is non-negotiable for us.',

    'partner.process.title': 'How Collaboration Works Concretely',
    'partner.process.subtitle': 'Typical Process',
    'partner.process.step1.title': 'Your customer expresses an AI need',
    'partner.process.step1.example1': 'e.g., "We want to use AI in sales"',
    'partner.process.step1.example2': 'or "We need an internal AI assistant"',
    'partner.process.step2.title': 'You decide whether to involve BROVKO',
    'partner.process.step2.note1': 'No obligation',
    'partner.process.step2.note2': 'No automatic referral',
    'partner.process.step3.title': 'Joint Clarification',
    'partner.process.step3.point1': 'Role of the partner',
    'partner.process.step3.point2': 'Role of BROVKO',
    'partner.process.step3.point3': 'Appearance towards the customer',
    'partner.process.step4.title': 'Implementation',
    'partner.process.step4.point1': 'BROVKO delivers AI know-how, architecture, implementation',
    'partner.process.step4.point2': 'You remain the main contact',

    'partner.models.title': 'Possible Cooperation Models',
    'partner.models.intro': 'We deliberately keep the models simple and flexible:',
    'partner.models.model1.title': 'Model 1: Subcontractor',
    'partner.models.model1.point1': 'You are the prime contractor',
    'partner.models.model1.point2': 'BROVKO works in the background',
    'partner.models.model1.ideal': 'Ideal if you want maximum customer visibility',
    'partner.models.model2.title': 'Model 2: Co-Branding',
    'partner.models.model2.point1': 'Joint appearance',
    'partner.models.model2.point2': '"Powered by BROVKO"',
    'partner.models.model2.ideal': 'Transparent to the customer',
    'partner.models.model3.title': 'Model 3: White-Label',
    'partner.models.model3.point1': 'BROVKO delivers the AI service',
    'partner.models.model3.point2': 'You appear entirely as the provider',
    'partner.models.note': 'The model is decided per project – not across the board.',

    'partner.revenue.title': 'Revenue & Fairness',
    'partner.revenue.intro': 'Fairness is not a marketing term for us, but a structural principle.',
    'partner.revenue.point1': 'Transparent revenue sharing for brokered or joint projects',
    'partner.revenue.point2': 'Clear agreements before project start',
    'partner.revenue.point3': 'No hidden dependencies',
    'partner.revenue.point4': 'No subsequent changes',
    'partner.revenue.goal': 'Goal: A collaboration that makes economic sense for both sides.',

    'partner.benefits.title': 'What the Partner Concretely Gains',
    'partner.benefits.point1': 'Expansion of service portfolio without own AI team',
    'partner.benefits.point2': 'Professional appearance for AI inquiries',
    'partner.benefits.point3': 'Reduction of implementation and reputation risks',
    'partner.benefits.point4': 'Additional revenues through AI projects',
    'partner.benefits.point5': 'Stronger customer loyalty through credible answers',
    'partner.benefits.quote': '"We can offer AI without having stomach aches."',
    'partner.benefits.quoteNote': 'Many partners say after the first projects:',

    'partner.suitability.title': 'Who the Program Is Suitable For – and Who Not',
    'partner.suitability.suitable.title': 'Suitable for:',
    'partner.suitability.suitable.point1': 'IT service providers with existing customers',
    'partner.suitability.suitable.point2': 'System houses, integrators, IT consultants',
    'partner.suitability.suitable.point3': 'Companies that want to protect long-term customer relationships',
    'partner.suitability.notSuitable.title': 'Not suitable for:',
    'partner.suitability.notSuitable.point1': 'Pure lead collectors',
    'partner.suitability.notSuitable.point2': 'Brokers without customer loyalty',
    'partner.suitability.notSuitable.point3': 'Partners who only seek short-term margins',

    'partner.form.title': 'Next Step',
    'partner.form.subtitle': 'The partner program is not a self-service product. The next step is always: a non-binding conversation to check whether mindset, standards, and approach fit together.',
    'partner.form.note': 'Only then do both sides decide whether and how a collaboration makes sense.',
    'partner.form.name': 'Name',
    'partner.form.company': 'Company',
    'partner.form.email': 'Email',
    'partner.form.message': 'Short Message',
    'partner.form.messagePlaceholder': 'Optional: What particularly interests you?',
    'partner.form.submit': 'Request Conversation',
    'partner.form.submitting': 'Sending...',
    'partner.form.privacy': 'Your data will be treated confidentially and not shared with third parties.',
    'partner.form.errors.name': 'Please enter your name',
    'partner.form.errors.company': 'Please enter your company',
    'partner.form.errors.email': 'Please enter an email address',
    'partner.form.errors.emailInvalid': 'Please enter a valid email address',
    'partner.form.errors.submit': 'There was an error submitting. Please try again.',
    'partner.form.success.title': 'Thank you for your inquiry',
    'partner.form.success.message': 'We will get back to you shortly.',
  }
};